export namespace Spektrix {
  export interface Country {
    isoCode: string
    name: string
    postcodeRequired: boolean
    displayPriority: number
    administrativeDivisions: AdministrativeDivision[]
  }

  export interface AdministrativeDivision {
    name: string
    isoCode: string
    displayPriority: number
  }

  export interface Address {
    country: Country
    administrativeDivision: AdministrativeDivision
    name: string
    line1: string
    line2: string
    line3: string
    line4: string
    line5: string
    postcode: string
    town: string
  }

  export interface PostcodeLookupResult {
    /** A summary of the address */
    description: string
    /** The postcode lookup id for the address. Use this to get the full address. */
    id: string
  }

  export interface AgentCredit {
    creditLimit: any
    spentCredit: any
    availableCredit: any
  }

  export interface Band {
    name: string
    hexCode: string
    id: string
    attributes: any
  }

  export interface MultibuyOffer {
    offer: Offer
    potentialEvents: Event[]
  }

  export interface DeliveryOption {
    fee: any
    type: DeliveryType
  }

  export interface Offer {
    betterOffer: Offer
    description: string
    endDate: Date
    htmlDescription: string
    imageUrl: string
    name: string
    promoCodes: string[]
    requiresPromoCode: boolean
    startDate: Date
    thumbnailUrl: string
    type: OfferType
    interfaces: Interface[]
    discountedPrices: PriceList
    status: OfferStatus
    multibuyMinimumEvents: number
    xForYX: number
    xForYY: number
    packageItems: any
    statusString: string
    id: string
    attributes: any
  }

  export interface Delivery {
    merchandise: MerchandiseItem[]
    tickets: Ticket[]
    type: DeliveryType
    emailAddress: string
    address: Address
  }

  export interface BasketCommission {
    waived: boolean
    amount: any
  }

  export interface TicketSubscription {
    id: string
    ticketSubscriptionStructureName: string
    pricingSetName: string
    price: any
  }

  export interface AgentCustomer {
    name: string
    firstName: string
    lastName: string
    addressLine: string
    agentOrderReference: string
  }

  export interface CustomPayment {
    name: string
    amount: any
    attributes: CustomPaymentAttribute[]
  }

  export interface Customer {
    age: number
    allStatements: DataProtectionStatement[]
    agreedStatements: DataProtectionStatement[]
    addresses: CustomerAddress[]
    billingAddress: CustomerAddress
    deliveryAddress: CustomerAddress
    birthDate: Date
    email: string
    firstName: string
    lastName: string
    mobile: string
    name: string
    phone: string
    allTagsByGroup: CustomerTagGroup[]
    tagsByGroup: TagGroup[]
    tags: Tag[]
    allTags: CustomerTag[]
    printAtHomeDocuments: PrintAtHomeDocument[]
    title: string
    passwordSet: boolean
    giftAidDeclarationRequired: boolean
    ticketSubscriptions: CustomerInventoryTicketSubscription[]
    subscriptions: CustomerSubscription[]
    orders: Order[]
    storedCards: StoredCard[]
    id: string
    attributes: any
    creditBalance: Money
  }

  export interface MerchandiseItem {
    barcode: string
    stockItem: StockItem
    delivered: boolean
    offer: Offer
    customItems: CustomItem[]
    /** The amount this item has been discounted by from any offers. */
    discount: any
    /** The original price of the item, before it was overridden. */
    originalPrice: any
    /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
    price: any
    /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
    priceOverridden: boolean
    /** The total price of the item after any commissions, discounts etc. have been applied. */
    total: any
    id: string
  }

  export interface Donation {
    amount: any
    fund: Fund
    recognitionName: string
    isAnonymous: boolean
    tributeTypeId: string
    tributeName: string
    id: string
  }

  export interface Ticket {
    /** The Agent Discount for this ticket. */
    agentDiscount: any
    band: Band
    event: Event
    instance: Instance
    planId: string
    seat: Seat
    seatName: string
    planName: string
    /** A collection of seat IDs that become isolated as a result of this selection */
    isolatedSeatIds: number[]
    voucherRedemptionInfo: VoucherRedemptionInfo
    type: TicketType
    ticketType: TicketType
    attributes: any
    delivered: boolean
    barcode: string
    offer: Offer
    customItems: CustomItem[]
    /** The amount this item has been discounted by from any offers. */
    discount: any
    /** The original price of the item, before it was overridden. */
    originalPrice: any
    /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
    price: any
    /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
    priceOverridden: boolean
    /** The total price of the item after any commissions, discounts etc. have been applied. */
    total: any
    id: string
  }

  export interface MembershipSubscription {
    autoRenew: boolean
    membership: Membership
    startDate: Date
    expiryDate: Date
    isRenewal: boolean
    relatedDonationId: string
    customItems: CustomItem[]
    /** The amount this item has been discounted by from any offers. */
    discount: any
    /** The original price of the item, before it was overridden. */
    originalPrice: any
    /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
    price: any
    /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
    priceOverridden: boolean
    /** The total price of the item after any commissions, discounts etc. have been applied. */
    total: any
    id: string
  }

  export interface GiftVoucher {
    toName: string
    message: string
    fromName: string
    amount: any
    sendDate: Date
    deliveryType: VoucherDeliveryType
    /** The email address that the gift voucher is delivered to */
    deliveryEmailAddress: string
    id: string
  }

  export interface Charge {
    name: string
    amount: any
  }

  export interface Basket {
    multibuyOffers: MultibuyOffer[]
    totalDiscount: any
    merchandiseDeliveryOptions: DeliveryOption[]
    defaultCardPaymentFee: any
    potentialOffers: Offer[]
    ticketDeliveryOptions: DeliveryOption[]
    promoCode: string
    id: string
    basketId: string
    merchandiseDelivery: Delivery
    transactionCommission: BasketCommission
    offers: Offer[]
    ticketDelivery: Delivery
    ticketSubscriptions: TicketSubscription[]
    disableWebComponents: boolean
    /** A value representing this basket. This will change any time the basket changes, and can be used to decide whether to redraw the basket. */
    hash: string
    agentCustomer: AgentCustomer
    attributes: any
    customPayments: CustomPayment[]
    customer: Customer
    merchandiseItems: MerchandiseItem[]
    donations: Donation[]
    notes: string
    tickets: Ticket[]
    membershipSubscriptions: MembershipSubscription[]
    giftVouchers: GiftVoucher[]
    total: any
    charges: Charge[]
  }

  export interface BasketPatch {
    customer: string
    agentCustomer: AgentCustomerPatch
    ticketDelivery: NewDelivery
    merchandiseDelivery: NewDelivery
    waiveTransactionCommission: boolean
    promoCode: string
    attributes: any
  }

  export interface OrderConfirmation {
    sendConfirmationEmail: boolean
    payWithStoredCardId: string
    payWithStoredCardCv2: string
    paymentChannel: PaymentChannel
    payWithDefaultStoredCard: boolean
    haveStoredCardId: boolean
  }

  export interface Order {
    deliveries: Delivery[]
    ticketSubscriptions: OrderHistoryTicketSubscription[]
    firstTransactionDate: Date
    firstTransactionDateUtc: Date
    lastTransactionDate: Date
    lastTransactionDateUtc: Date
    totalPaymentCharges: any
    totalDeliveryCharges: any
    totalTransactionCharges: any
    payments: Payment[]
    refunds: Payment[]
    printAtHomeDocuments: PrintAtHomeDocument[]
    id: string
    attributes: any
    total: any
    customer: Customer
    merchandiseItems: MerchandiseItem[]
    donations: Donation[]
    notes: string
    tickets: Ticket[]
    membershipSubscriptions: MembershipSubscription[]
    giftVouchers: GiftVoucher[]
    charges: Charge[]
  }

  export interface DiscountInfo {
    basketTotal: any
    totalDiscount: any
    transactionCommission: BasketCommission
  }

  export interface Fund {
    id: string
    name: string
    description: string
    web: boolean
    code: string
    defaultDonationAmount: any
    isActive: boolean
  }

  export interface Event {
    description: string
    htmlDescription: string
    duration: number
    imageUrl: string
    isOnSale: boolean
    name: string
    instanceDates: string
    relatedEvents: Event[]
    relatedStockItems: StockItem[]
    instances: Instance[]
    thumbnailUrl: string
    webEventId: string
    id: string
    attributes: any
    firstInstanceDateTime: Date
    firstInstanceDateTimeUtc: Date
    lastInstanceDateTime: Date
    lastInstanceDateTimeUtc: Date
    altText: string
  }

  export interface Instance {
    isOnSale: boolean
    planId: string
    priceList: PriceList
    event: Event
    start: Date
    startUtc: Date
    startSellingAtWeb: Date
    startSellingAtWebUtc: Date
    startSellingAtAgents: Date
    startSellingAtAgentsUtc: Date
    startSellingAtFundraising: Date
    startSellingAtFundraisingUtc: Date
    startSellingAtSales: Date
    startSellingAtSalesUtc: Date
    stopSellingAtWeb: Date
    stopSellingAtWebUtc: Date
    stopSellingAtAgents: Date
    stopSellingAtAgentsUtc: Date
    stopSellingAtFundraising: Date
    stopSellingAtFundraisingUtc: Date
    stopSellingAtSales: Date
    stopSellingAtSalesUtc: Date
    webInstanceId: string
    cancelled: boolean
    id: string
    attributes: any
    hasBestAvailableOverlay: boolean
  }

  export interface Seat {
    id: string
    name: string
    row: string
    number: number
    previousSeatId: number
    previousIsAcrossAisle: boolean
    nextIsAcrossAisle: boolean
    nextSeatId: number
    x: number
    y: number
    tabOrder: number
    attributes: any
  }

  export interface VoucherRedemptionInfo {
    ticketSubscriptionName: string
    voucherPrice: any
    voucherCommission: any
  }

  export interface TicketType {
    name: string
    id: string
    attributes: any
  }

  export interface CustomItem {
    name: string
    amount: any
    displayMode: CustomItemDisplayMode
  }

  export interface NewDonation {
    amount: any
    fundId: string
    recognitionName: string
    isAnonymous: boolean
    tributeTypeId: string
    tributeName: string
  }

  export interface DonationPatch {
    amount: any
    fundId: string
    recognitionName: string
    isAnonymous: boolean
    tributeTypeId: string
    tributeName: string
  }

  export interface NewTicket {
    instance: string
    seat?: string
    type: string
    seatingPlan?: string
    ticketType?: string
    attributes?: any
    overriddenPrice?: any
  }

  export interface StockItem {
    description: string
    htmlDescription: string
    imageUrl: string
    name: string
    postageAndPacking: any
    price: any
    stockLevel: number
    thumbnailUrl: string
    id: string
    attributes: any
  }

  export interface NewMerchandiseItem {
    stockItem: string
    reference: string
    overriddenPrice: any
  }

  export enum VoucherDeliveryType {
    CustomerEmail = 1,
    OtherEmail = 2,
    Print = 3,
  }

  export interface NewGiftVoucher {
    amount: any
    toName: string
    fromName: string
    message: string
    sendDate: Date
    deliveryType: VoucherDeliveryType
    deliveryEmailAddress: string
  }

  export interface GiftVoucherPatch {
    amount: any
    toName: string
    fromName: string
    sendDate: Date
    deliveryType: VoucherDeliveryType
    deliveryEmailAddress: string
    message: string
  }

  export interface TicketPatch {
    type: string
    attributes: any
    ticketType: string
    overriddenPrice: any
  }

  export interface MerchandiseItemPatch {
    reference: string
    overriddenPrice: any
  }

  export interface BestAvailableSettings {
    instance: string
    priceBands: string[]
    seatingAreas: string[]
    quantity: number
  }

  export interface Membership {
    description: string
    htmlDescription: string
    id: string
    imageUrl: string
    name: string
    thumbnailUrl: string
    attributes: any
    /** For Variable Memberships ‘Price’ will be ‘Min Price’. */
    price: any
    renewalPrice: any
    /** For Variable Memberships, only available when IsVariable is true. */
    maxPrice: any
    altText: string
    isGiftable: boolean
    isVariable: boolean
    canAutoRenew: boolean
    /** Expressed as structured data where ‘expiryType’ defines how data should be displayed. WeeksDays = Membership duration is {weeks} weeks, {days} days. YearsMonthsDays = Membership duration is {years} years, {months} months, {days} days. Fixed = Membership expires on {days} day of {month}. OpenEnded = does not expire. */
    membershipPeriod: MembershipPeriod
  }

  export interface NewMembershipSubscription {
    /** For Variable Memberships. Use user selected value. */
    overriddenPrice?: number
    membership: string
    autoRenew: boolean
  }

  export interface MembershipSubscriptionPatch {
    /** For Variable Memberships. Use user selected value. */
    overriddenPrice: any
    autoRenew: boolean
  }

  export interface NewTicketSubscription {
    pricingSetId: string
    quantity: number
  }

  export interface NewBasket {
    customer: string
    agentCustomer: AgentCustomerPatch
    ticketDelivery: NewDelivery
    merchandiseDelivery: NewDelivery
    waiveTransactionCommission: boolean
    promoCode: string
    attributes: any
  }

  export interface NewCustomPayment {
    amount: any
    paymentTypeId: string
    paymentAttributes: NewCustomPaymentAttribute[]
  }

  export interface CustomPaymentAttribute {
    name: string
    value: string
  }

  export interface NewCustomer {
    agreedStatements: string[]
    addresses: NewCustomerAddress[]
    birthDate: Date
    email: string
    firstName: string
    friendlyId: string
    lastName: string
    mobile: string
    phone: string
    title: string
    tags: string[]
    password: string
    attributes: any
  }

  export interface DataProtectionStatement {
    id: string
    text: string
    agreed: boolean
  }

  export interface CustomerAddress {
    isBilling: boolean
    isDelivery: boolean
    id: string
    country: Country
    administrativeDivision: AdministrativeDivision
    name: string
    line1: string
    line2: string
    line3: string
    line4: string
    line5: string
    postcode: string
    town: string
  }

  export interface CustomerTagGroup {
    id: number
    name: string
    customerTags: CustomerTag[]
  }

  export interface TagGroup {
    id: string
    name: string
    description: string
    web: boolean
    tags: Tag[]
  }

  export interface Tag {
    id: string
    name: string
  }

  export interface CustomerTag {
    tagged: boolean
    id: string
    name: string
  }

  export interface PrintAtHomeDocument {
    instance: Instance
    stockItem: StockItem
    id: string
  }

  export interface CustomerInventoryTicketSubscription {
    ticketSubscriptionStructureName: string
    pricingSetName: string
    price: any
    totalVouchers: number
    vouchersRedeemed: number
    issueDate: Date
    expiryDate: Date
  }

  export interface CustomerSubscription {
    autoRenew: boolean
    membership: Membership
    startDate: Date
    expiryDate: Date
    canRenew: boolean
    eligibleOffers: Offer[]
  }

  export interface StoredCard {
    id: string
    address: Address
    isDefault: boolean
    maskedNumber: string
    cardHolderName: string
    expiryDate: Date
    type: CardType
    isPending: boolean
  }

  export interface Money {
    tinyValue: any
  }

  export interface CustomerPatch {
    email: string
    firstName: string
    lastName: string
    mobile: string
    phone: string
    title: string
    giftAidConfirmed: boolean
    birthDate: Date
    attributes: any
  }

  export interface Credentials {
    email: string
    password: string
  }

  export interface NewPasswordInfo {
    oldPassword: string
    newPassword: string
  }

  export interface NewPasswordFromLink {
    token: string
    newPassword: string
  }

  export interface NewCustomerAddress {
    isDelivery: boolean
    isBilling: boolean
    country: string
    administrativeDivision: string
    name: string
    line1: string
    line2: string
    line3: string
    line4: string
    line5: string
    postcode: string
    town: string
  }

  export interface AddressPatch {
    country: string
    administrativeDivision: string
    name: string
    line1: string
    line2: string
    line3: string
    line4: string
    line5: string
    postcode: string
    town: string
  }

  export interface NewCustomerTag {
    id: string
  }

  export interface NewCustomerDataProtectionStatement {
    id: string
  }

  export interface OrderHistoryTicketSubscription {
    ticketSubscriptionStructureName: string
    pricingSetName: string
    price: any
  }

  export interface Payment {
    cardNumber: string
    amount: any
    type: string
  }

  export enum CardType {
    Visa = 0,
    MasterCard = 1,
    VisaDebitDelta = 2,
    Solo = 3,
    UkMaestro = 4,
    VisaElectron = 5,
    AmericanExpress = 6,
    DinersClub = 7,
    Jcb = 8,
    VisaPurchasing = 9,
    InternationalMaestro = 10,
    VisaAtm = 11,
    ArvalPhh = 12,
    Laser = 13,
    Duet = 14,
    Discover = 15,
    Unknown = 16,
    ApplePay = 17,
    ApplePayVisa = 18,
    ApplePayMastercard = 19,
    ApplePayMaestro = 20,
    ApplePayAmericanExpress = 21,
    GooglePayVisa = 22,
    GooglePayMastercard = 23,
    GooglePayDiscover = 24,
    GooglePayAmericanExpress = 25,
    Interac = 26,
    UnionPay = 27,
    ApplePayDiscover = 28,
    GooglePayMaestro = 29,
    ApplePayInterac = 30,
    GooglePayInterac = 31,
    ApplePayJcb = 32,
    GooglePay = 33,
    EftposAustralia = 34,
    ApplePayEftposAustralia = 35,
    GooglePayEftposAustralia = 36,
    EftposAustraliaChq = 37,
    EftposAustraliaSav = 38,
    Girocard = 39,
    ApplePayGirocard = 40,
  }

  export interface AttributeDefinition {
    name: string
    isRequired: boolean
    type: string
  }

  export interface CustomPaymentType {
    name: string
    id: string
    isRefund: boolean
    attributeDefinitions: AttributeDefinition[]
  }

  export interface NameValuePairOfString {
    name: string
    value: string
  }

  export enum Interface {
    Sales = 0,
    Web = 1,
    Agents = 2,
    Fundraising = 3,
    All = 4,
  }

  export interface PriceList {
    prices: Price[]
    id: string
  }

  export interface EventInstanceAvailabilityDto {
    eventInstanceId: string
    start: Date
    startUtc: Date
    capacity: number
    availability: PriceBandAvailabilityDto[]
  }

  export interface EventAvailabilityDto {
    data: EventInstanceAvailabilityDto[]
  }

  export enum SeatingPlanType {
    Group = 0,
    Reserved = 1,
    Unreserved = 2,
  }

  export interface Venue {
    name: string
    address: string
    id: string
    attributes: any
  }

  export interface ApiLayoutOverlay {
    backgroundImageUrl: string
    x: number
    y: number
  }

  export interface Plan {
    type: SeatingPlanType
    name: string
    venue: Venue
    layoutOverlay: ApiLayoutOverlay
    backgroundImageUrl: string
    x: number
    y: number
    areas: Plan[]
    seats: Seat[]
    capacity: number
    maxZoomWeb: any
    maxZoomMobile: any
    vertices: SeatingPlanVertices
    id: string
  }

  export interface SeatingPlanVertices {
    topLeft: string
    topRight: string
    bottomRight: string
    bottomLeft: string
  }

  export interface LockedSeatQuantity {
    quantity: number
    lockType: LockTypeInformation
  }

  export interface InstancePlanStatus {
    name: string
    available: number
    wheelchairAvailable: number
    specialAvailable: number
    capacity: number
    bands: Band[]
    inBasket: number
    inOtherBasket: number
    locked: number
    reserved: number
    sold: number
    scanned: number
    masked: number
    unavailable: number
    lockInformation: LockedSeatQuantity[]
    lockInfoAvailable: LockedSeatQuantity[]
    lockInfoSpecialAvailable: LockedSeatQuantity[]
  }

  export type IHttpActionResult = any

  export interface Price {
    isBandDefault: boolean
    amount: any
    ticketType: TicketType
    priceBand: Band
    customItems: CustomItem[]
    id: string
  }

  export interface PriceListPatch {
    prices: PricePatch[]
  }

  export interface MembershipPeriod {
    expiryType: MembershipPeriodExpiryType
    years: number
    months: number
    weeks: number
    days: number
  }

  export enum OfferType {
    MultiBuy = 0,
    XForY = 1,
    Package = 2,
  }

  export type OfferStatus = any

  export interface VenueOverview {
    id: string
    name: string
    address: string
  }

  export interface PlanOverview {
    id: string
    name: string
    capacity: number
    backgroundImageUrl: string
    x: number
    y: number
    maxZoomWeb: any
    maxZoomMobile: any
    verticesJson: string
    type: SeatingPlanType
    vertices: SeatingPlanVertices
    venue: VenueOverview
    areas: PlanOverview[]
  }

  export interface Config {
    currency: string
    currencyCharacter: string
    culture: string
    isolatedSeatMode: string
  }

  export interface PricingSet {
    id: string
    name: string
    price: any
  }

  export interface TicketSubscriptionStructure {
    name: string
    description: string
    onSaleWebChannel: Date
    onSaleWebChannelUtc: Date
    offSaleWebChannel: Date
    offSaleWebChannelUtc: Date
    pricingSets: PricingSet[]
  }

  export interface TributeType {
    id: string
    name: string
  }

  export enum DeliveryType {
    Postal = 0,
    Cobo = 1,
    PrintAtHome = 2,
    Agent = 3,
  }

  export interface AgentCustomerPatch {
    name: string
    firstName: string
    lastName: string
    addressLine: string
    agentOrderReference: string
  }

  export interface NewDelivery {
    type: DeliveryType
    address: NewAddress
  }

  export enum PaymentChannel {
    Web = 0,
    Moto = 1,
    ContinuousAuthority = 2,
  }

  export enum CustomItemDisplayMode {
    IncludedInPrice = 0,
    IncludedInPriceButDisplayed = 1,
    SeparateFromPrice = 2,
  }

  export interface NewCustomPaymentAttribute {
    name: string
    value: string
  }

  export interface PriceBandAvailabilityDto {
    priceBand: string
    status: SeatStatusType
    count: number
  }

  export interface LockTypeInformation {
    name: string
    id: string
    availableOnWeb: boolean
    availableToAgents: boolean
    requiresEligibility: boolean
  }

  export interface PricePatch {
    ticketTypeId: string
    priceBandId: string
    updatedPrice: any
  }

  export enum MembershipPeriodExpiryType {
    YearsMonthsDays = 0,
    WeeksDays = 1,
    Fixed = 2,
    OpenEnded = 3,
  }

  export interface NewAddress {
    country: string
    administrativeDivision: string
    name: string
    line1: string
    line2: string
    line3: string
    line4: string
    line5: string
    postcode: string
    town: string
  }

  export enum SeatStatusType {
    Sold = 1,
    Available = 2,
    Selected = 4,
    Reserved = 5,
    Scanned = 6,
    HeldFixedSeries = 7,
  }

  export interface API_GET {
    /**
     * Addresses
     * Returns the full address for a given ID
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-addresses-postcodeLookupId?mode=ShowAll
     */
    "v3/addresses/{postcodeLookupId}": {
      input: {
        /** The ID from GET v3/addresses?postcode={postcode} — Required */
        postcodeLookupId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        country?: Country
        administrativeDivision?: AdministrativeDivision
        name?: string
        line1?: string
        line2?: string
        line3?: string
        line4?: string
        line5?: string
        postcode?: string
        town?: string
      }
      output: Address
    }

    /**
     * Addresses
     * Returns a list of addresses matching a given postcode. (UK Addresses only)
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-addresses_postcode?mode=ShowAll
     */
    "v3/addresses?postcode={postcode}": {
      input: {
        /** The postcode to use — Required */
        postcode: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        /** A summary of the address */
        description?: string
        /** The postcode lookup id for the address. Use this to get the full address. */
        id?: string
      }
      output: PostcodeLookupResult[]
    }

    /**
     * AgentCredit
     * Allows an agent to determine how much of their credit limit has been used, and how much remains.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-credit?mode=ShowAll
     */
    "v3/credit": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        creditLimit?: any
        spentCredit?: any
        availableCredit?: any
      }
      output: AgentCredit
    }

    /**
     * Bands
     * Returns a list of price bands in the system
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-bands?mode=ShowAll
     */
    "v3/bands": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        hexCode?: string
        id?: string
        attributes?: any
      }
      output: Band[]
    }

    /**
     * Basket
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-payment?mode=ShowAll
     */
    "v3/basket/payment": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Return the current contents of the Basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket?mode=ShowAll
     */
    "v3/basket": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        multibuyOffers?: MultibuyOffer[]
        totalDiscount?: any
        merchandiseDeliveryOptions?: DeliveryOption[]
        defaultCardPaymentFee?: any
        potentialOffers?: Offer[]
        ticketDeliveryOptions?: DeliveryOption[]
        promoCode?: string
        id?: string
        basketId?: string
        merchandiseDelivery?: Delivery
        transactionCommission?: BasketCommission
        offers?: Offer[]
        ticketDelivery?: Delivery
        ticketSubscriptions?: TicketSubscription[]
        disableWebComponents?: boolean
        /** A value representing this basket. This will change any time the basket changes, and can be used to decide whether to redraw the basket. */
        hash?: string
        agentCustomer?: AgentCustomer
        attributes?: any
        customPayments?: CustomPayment[]
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        total?: any
        charges?: Charge[]
      }
      output: Basket
    }

    /**
     * Basket
     * Returns pricing information about the current basket with the specified Membership added, without persisting the changes. This allows for the calculation of upsell information.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-potentialDiscount_membershipId?mode=ShowAll
     */
    "v3/basket/potentialDiscount?membershipId={membershipId}": {
      input: {
        /** The ID from — Required */
        membershipId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        basketTotal?: any
        totalDiscount?: any
        transactionCommission?: BasketCommission
      }
      output: DiscountInfo
    }

    /**
     * Basket
     * Return details of the donations in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-donations?mode=ShowAll
     */
    "v3/basket/donations": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        amount?: any
        fund?: Fund
        recognitionName?: string
        isAnonymous?: boolean
        tributeTypeId?: string
        tributeName?: string
        id?: string
      }
      output: Donation[]
    }

    /**
     * Basket
     * Return details of the tickets in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-tickets?mode=ShowAll
     */
    "v3/basket/tickets": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        /** The Agent Discount for this ticket. */
        agentDiscount?: any
        band?: Band
        event?: Event
        instance?: Instance
        planId?: string
        seat?: Seat
        seatName?: string
        planName?: string
        /** A collection of seat IDs that become isolated as a result of this selection */
        isolatedSeatIds?: number[]
        voucherRedemptionInfo?: VoucherRedemptionInfo
        type?: TicketType
        ticketType?: TicketType
        attributes?: any
        delivered?: boolean
        barcode?: string
        offer?: Offer
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: Ticket[]
    }

    /**
     * Basket
     * Return the suggested donations for the current basket contents of the current customer
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-donations-to-prompt?mode=ShowAll
     */
    "v3/basket/donations-to-prompt": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        description?: string
        web?: boolean
        code?: string
        defaultDonationAmount?: any
        isActive?: boolean
      }
      output: Fund[]
    }

    /**
     * Basket
     * Return the details of a specific donation in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-donations-donationId?mode=ShowAll
     */
    "v3/basket/donations/{donationId}": {
      input: {
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        amount?: any
        fund?: Fund
        recognitionName?: string
        isAnonymous?: boolean
        tributeTypeId?: string
        tributeName?: string
        id?: string
      }
      output: Donation
    }

    /**
     * Basket
     * Return all merchandise items from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-merchandise?mode=ShowAll
     */
    "v3/basket/merchandise": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        barcode?: string
        stockItem?: StockItem
        delivered?: boolean
        offer?: Offer
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MerchandiseItem[]
    }

    /**
     * Basket
     * Return all gift vouchers in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-gift-vouchers?mode=ShowAll
     */
    "v3/basket/gift-vouchers": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        toName?: string
        message?: string
        fromName?: string
        amount?: any
        sendDate?: Date
        deliveryType?: VoucherDeliveryType
        /** The email address that the gift voucher is delivered to */
        deliveryEmailAddress?: string
        id?: string
      }
      output: GiftVoucher[]
    }

    /**
     * Basket
     * Return the details of a specific gift voucher in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/basket/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        toName?: string
        message?: string
        fromName?: string
        amount?: any
        sendDate?: Date
        deliveryType?: VoucherDeliveryType
        /** The email address that the gift voucher is delivered to */
        deliveryEmailAddress?: string
        id?: string
      }
      output: GiftVoucher
    }

    /**
     * Basket
     * Return the details of all memberships in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-membership-subscriptions?mode=ShowAll
     */
    "v3/basket/membership-subscriptions": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        autoRenew?: boolean
        membership?: Membership
        startDate?: Date
        expiryDate?: Date
        isRenewal?: boolean
        relatedDonationId?: string
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MembershipSubscription[]
    }

    /**
     * Basket
     * Return the details of a specific membership in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/basket/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        autoRenew?: boolean
        membership?: Membership
        startDate?: Date
        expiryDate?: Date
        isRenewal?: boolean
        relatedDonationId?: string
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MembershipSubscription
    }

    /**
     * Basket
     * Return all ticket subscriptions in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-ticket-subscriptions?mode=ShowAll
     */
    "v3/basket/ticket-subscriptions": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        ticketSubscriptionStructureName?: string
        pricingSetName?: string
        price?: any
      }
      output: TicketSubscription[]
    }

    /**
     * Basket
     * Return the details of a specific ticket subscription in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-basket-ticket-subscriptions-ticketSubscriptionId?mode=ShowAll
     */
    "v3/basket/ticket-subscriptions/{ticketSubscriptionId}": {
      input: {
        /** Required */
        ticketSubscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        ticketSubscriptionStructureName?: string
        pricingSetName?: string
        price?: any
      }
      output: TicketSubscription
    }

    /**
     * Baskets
     * Return the details of a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id?mode=ShowAll
     */
    "v3/baskets/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        multibuyOffers?: MultibuyOffer[]
        totalDiscount?: any
        merchandiseDeliveryOptions?: DeliveryOption[]
        defaultCardPaymentFee?: any
        potentialOffers?: Offer[]
        ticketDeliveryOptions?: DeliveryOption[]
        promoCode?: string
        id?: string
        basketId?: string
        merchandiseDelivery?: Delivery
        transactionCommission?: BasketCommission
        offers?: Offer[]
        ticketDelivery?: Delivery
        ticketSubscriptions?: TicketSubscription[]
        disableWebComponents?: boolean
        /** A value representing this basket. This will change any time the basket changes, and can be used to decide whether to redraw the basket. */
        hash?: string
        agentCustomer?: AgentCustomer
        attributes?: any
        customPayments?: CustomPayment[]
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        total?: any
        charges?: Charge[]
      }
      output: Basket
    }

    /**
     * Baskets
     * Returns pricing information about the current basket with the specified Membership added, without persisting the changes. This allows for the calculation of upsell information.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-potentialDiscount_membershipId?mode=ShowAll
     */
    "v3/baskets/{id}/potentialDiscount?membershipId={membershipId}": {
      input: {
        /** Required */
        id: string
        /** The ID from — Required */
        membershipId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        basketTotal?: any
        totalDiscount?: any
        transactionCommission?: BasketCommission
      }
      output: DiscountInfo
    }

    /**
     * Baskets
     * Return the suggested donations of a basket contents for the added customer record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-donations-to-prompt?mode=ShowAll
     */
    "v3/baskets/{id}/donations-to-prompt": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        description?: string
        web?: boolean
        code?: string
        defaultDonationAmount?: any
        isActive?: boolean
      }
      output: Fund[]
    }

    /**
     * Baskets
     * Return the details of all custom payments added to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-custom-payments?mode=ShowAll
     */
    "v3/baskets/{id}/custom-payments": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        amount?: any
        attributes?: CustomPaymentAttribute[]
      }
      output: CustomPayment[]
    }

    /**
     * Baskets
     * Return details of the donations in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-donations-donationId?mode=ShowAll
     */
    "v3/baskets/{id}/donations/{donationId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        amount?: any
        fund?: Fund
        recognitionName?: string
        isAnonymous?: boolean
        tributeTypeId?: string
        tributeName?: string
        id?: string
      }
      output: Donation
    }

    /**
     * Baskets
     * Return details of the donations in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-donations?mode=ShowAll
     */
    "v3/baskets/{id}/donations": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        amount?: any
        fund?: Fund
        recognitionName?: string
        isAnonymous?: boolean
        tributeTypeId?: string
        tributeName?: string
        id?: string
      }
      output: Donation[]
    }

    /**
     * Baskets
     * Return all gift vouchers in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-gift-vouchers?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        toName?: string
        message?: string
        fromName?: string
        amount?: any
        sendDate?: Date
        deliveryType?: VoucherDeliveryType
        /** The email address that the gift voucher is delivered to */
        deliveryEmailAddress?: string
        id?: string
      }
      output: GiftVoucher[]
    }

    /**
     * Baskets
     * Return the details of a specific gift voucher in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        toName?: string
        message?: string
        fromName?: string
        amount?: any
        sendDate?: Date
        deliveryType?: VoucherDeliveryType
        /** The email address that the gift voucher is delivered to */
        deliveryEmailAddress?: string
        id?: string
      }
      output: GiftVoucher
    }

    /**
     * Baskets
     * Return the details of all memberships in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-membership-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        autoRenew?: boolean
        membership?: Membership
        startDate?: Date
        expiryDate?: Date
        isRenewal?: boolean
        relatedDonationId?: string
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * Return the details of a specific membership in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        autoRenew?: boolean
        membership?: Membership
        startDate?: Date
        expiryDate?: Date
        isRenewal?: boolean
        relatedDonationId?: string
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MembershipSubscription
    }

    /**
     * Baskets
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-merchandise?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        barcode?: string
        stockItem?: StockItem
        delivered?: boolean
        offer?: Offer
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: MerchandiseItem[]
    }

    /**
     * Baskets
     * Return details of the tickets in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-tickets?mode=ShowAll
     */
    "v3/baskets/{id}/tickets": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        /** The Agent Discount for this ticket. */
        agentDiscount?: any
        band?: Band
        event?: Event
        instance?: Instance
        planId?: string
        seat?: Seat
        seatName?: string
        planName?: string
        /** A collection of seat IDs that become isolated as a result of this selection */
        isolatedSeatIds?: number[]
        voucherRedemptionInfo?: VoucherRedemptionInfo
        type?: TicketType
        ticketType?: TicketType
        attributes?: any
        delivered?: boolean
        barcode?: string
        offer?: Offer
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: Ticket[]
    }

    /**
     * Baskets
     * Return the details of a specific ticket in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-tickets-ticketId?mode=ShowAll
     */
    "v3/baskets/{id}/tickets/{ticketId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        ticketId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        /** The Agent Discount for this ticket. */
        agentDiscount?: any
        band?: Band
        event?: Event
        instance?: Instance
        planId?: string
        seat?: Seat
        seatName?: string
        planName?: string
        /** A collection of seat IDs that become isolated as a result of this selection */
        isolatedSeatIds?: number[]
        voucherRedemptionInfo?: VoucherRedemptionInfo
        type?: TicketType
        ticketType?: TicketType
        attributes?: any
        delivered?: boolean
        barcode?: string
        offer?: Offer
        customItems?: CustomItem[]
        /** The amount this item has been discounted by from any offers. */
        discount?: any
        /** The original price of the item, before it was overridden. */
        originalPrice?: any
        /** The nominal price of the item, before any commissions, discounts etc. have been applied. */
        price?: any
        /** If true, the nominal price of the item (before commissions, discounts etc.) has been overridden. Price contains the overridden price; OriginalPrice contains the price before it was overridden */
        priceOverridden?: boolean
        /** The total price of the item after any commissions, discounts etc. have been applied. */
        total?: any
        id?: string
      }
      output: Ticket
    }

    /**
     * Baskets
     * Return all ticket subscriptions in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-ticket-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/ticket-subscriptions": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        ticketSubscriptionStructureName?: string
        pricingSetName?: string
        price?: any
      }
      output: TicketSubscription[]
    }

    /**
     * Baskets
     * Return the specified ticket subscription in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-baskets-id-ticket-subscriptions-ticketSubscriptionId?mode=ShowAll
     */
    "v3/baskets/{id}/ticket-subscriptions/{ticketSubscriptionId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        ticketSubscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        ticketSubscriptionStructureName?: string
        pricingSetName?: string
        price?: any
      }
      output: TicketSubscription
    }

    /**
     * Countries
     * Return a list of countries
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-countries?mode=ShowAll
     */
    "v3/countries": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isoCode?: string
        name?: string
        postcodeRequired?: boolean
        displayPriority?: number
        administrativeDivisions?: AdministrativeDivision[]
      }
      output: Country[]
    }

    /**
     * Countries
     * Return a specific country
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-countries-isoCode?mode=ShowAll
     */
    "v3/countries/{isoCode}": {
      input: {
        /** Required */
        isoCode: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isoCode?: string
        name?: string
        postcodeRequired?: boolean
        displayPriority?: number
        administrativeDivisions?: AdministrativeDivision[]
      }
      output: Country
    }

    /**
     * Customer
     * Return the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer?mode=ShowAll
     */
    "v3/customer": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        age?: number
        allStatements?: DataProtectionStatement[]
        agreedStatements?: DataProtectionStatement[]
        addresses?: CustomerAddress[]
        billingAddress?: CustomerAddress
        deliveryAddress?: CustomerAddress
        birthDate?: Date
        email?: string
        firstName?: string
        lastName?: string
        mobile?: string
        name?: string
        phone?: string
        allTagsByGroup?: CustomerTagGroup[]
        tagsByGroup?: TagGroup[]
        tags?: Tag[]
        allTags?: CustomerTag[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        title?: string
        passwordSet?: boolean
        giftAidDeclarationRequired?: boolean
        ticketSubscriptions?: CustomerInventoryTicketSubscription[]
        subscriptions?: CustomerSubscription[]
        orders?: Order[]
        storedCards?: StoredCard[]
        id?: string
        attributes?: any
        creditBalance?: Money
      }
      output: Customer
    }

    /**
     * Customer
     * Return a list of the current customers stored address details
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-addresses?mode=ShowAll
     */
    "v3/customer/addresses": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isBilling?: boolean
        isDelivery?: boolean
        id?: string
        country?: Country
        administrativeDivision?: AdministrativeDivision
        name?: string
        line1?: string
        line2?: string
        line3?: string
        line4?: string
        line5?: string
        postcode?: string
        town?: string
      }
      output: CustomerAddress[]
    }

    /**
     * Customer
     * Return the current users tags
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-tags?mode=ShowAll
     */
    "v3/customer/tags": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
      }
      output: Tag[]
    }

    /**
     * Customer
     * Return the current customers contact preferences
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-agreed-statements?mode=ShowAll
     */
    "v3/customer/agreed-statements": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        text?: string
        agreed?: boolean
      }
      output: DataProtectionStatement[]
    }

    /**
     * Customer
     * Returns a list of etickets for the current customer
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-print-at-home-documents?mode=ShowAll
     */
    "v3/customer/print-at-home-documents": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        instance?: Instance
        stockItem?: StockItem
        id?: string
      }
      output: PrintAtHomeDocument[]
    }

    /**
     * Customer
     * Return the current customers order history
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-orders?mode=ShowAll
     */
    "v3/customer/orders": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        deliveries?: Delivery[]
        ticketSubscriptions?: OrderHistoryTicketSubscription[]
        firstTransactionDate?: Date
        firstTransactionDateUtc?: Date
        lastTransactionDate?: Date
        lastTransactionDateUtc?: Date
        totalPaymentCharges?: any
        totalDeliveryCharges?: any
        totalTransactionCharges?: any
        payments?: Payment[]
        refunds?: Payment[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        id?: string
        attributes?: any
        total?: any
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        charges?: Charge[]
      }
      output: Order[]
    }

    /**
     * Customer
     * Return the current customers stored cards
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customer-stored-cards_includePending?mode=ShowAll
     */
    "v3/customer/stored-cards?includePending={includePending}": {
      input: {
        /** Default value is False */
        includePending?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        address?: Address
        isDefault?: boolean
        maskedNumber?: string
        cardHolderName?: string
        expiryDate?: Date
        type?: CardType
        isPending?: boolean
      }
      output: StoredCard[]
    }

    /**
     * Customers
     * Return a customers record by id
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id?mode=ShowAll
     */
    "v3/customers/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        age?: number
        allStatements?: DataProtectionStatement[]
        agreedStatements?: DataProtectionStatement[]
        addresses?: CustomerAddress[]
        billingAddress?: CustomerAddress
        deliveryAddress?: CustomerAddress
        birthDate?: Date
        email?: string
        firstName?: string
        lastName?: string
        mobile?: string
        name?: string
        phone?: string
        allTagsByGroup?: CustomerTagGroup[]
        tagsByGroup?: TagGroup[]
        tags?: Tag[]
        allTags?: CustomerTag[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        title?: string
        passwordSet?: boolean
        giftAidDeclarationRequired?: boolean
        ticketSubscriptions?: CustomerInventoryTicketSubscription[]
        subscriptions?: CustomerSubscription[]
        orders?: Order[]
        storedCards?: StoredCard[]
        id?: string
        attributes?: any
        creditBalance?: Money
      }
      output: Customer
    }

    /**
     * Customers
     * Return a customers record by email
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers_email?mode=ShowAll
     */
    "v3/customers?email={email}": {
      input: {
        /** Required */
        email: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        age?: number
        allStatements?: DataProtectionStatement[]
        agreedStatements?: DataProtectionStatement[]
        addresses?: CustomerAddress[]
        billingAddress?: CustomerAddress
        deliveryAddress?: CustomerAddress
        birthDate?: Date
        email?: string
        firstName?: string
        lastName?: string
        mobile?: string
        name?: string
        phone?: string
        allTagsByGroup?: CustomerTagGroup[]
        tagsByGroup?: TagGroup[]
        tags?: Tag[]
        allTags?: CustomerTag[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        title?: string
        passwordSet?: boolean
        giftAidDeclarationRequired?: boolean
        ticketSubscriptions?: CustomerInventoryTicketSubscription[]
        subscriptions?: CustomerSubscription[]
        orders?: Order[]
        storedCards?: StoredCard[]
        id?: string
        attributes?: any
        creditBalance?: Money
      }
      output: Customer
    }

    /**
     * Customers
     * Return a customer record by ticket barcode
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers_ticketBarcode?mode=ShowAll
     */
    "v3/customers?ticketBarcode={ticketBarcode}": {
      input: {
        /** Required */
        ticketBarcode: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        age?: number
        allStatements?: DataProtectionStatement[]
        agreedStatements?: DataProtectionStatement[]
        addresses?: CustomerAddress[]
        billingAddress?: CustomerAddress
        deliveryAddress?: CustomerAddress
        birthDate?: Date
        email?: string
        firstName?: string
        lastName?: string
        mobile?: string
        name?: string
        phone?: string
        allTagsByGroup?: CustomerTagGroup[]
        tagsByGroup?: TagGroup[]
        tags?: Tag[]
        allTags?: CustomerTag[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        title?: string
        passwordSet?: boolean
        giftAidDeclarationRequired?: boolean
        ticketSubscriptions?: CustomerInventoryTicketSubscription[]
        subscriptions?: CustomerSubscription[]
        orders?: Order[]
        storedCards?: StoredCard[]
        id?: string
        attributes?: any
        creditBalance?: Money
      }
      output: Customer
    }

    /**
     * Customers
     * Return a list of a customers stored address details
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-addresses?mode=ShowAll
     */
    "v3/customers/{id}/addresses": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isBilling?: boolean
        isDelivery?: boolean
        id?: string
        country?: Country
        administrativeDivision?: AdministrativeDivision
        name?: string
        line1?: string
        line2?: string
        line3?: string
        line4?: string
        line5?: string
        postcode?: string
        town?: string
      }
      output: CustomerAddress[]
    }

    /**
     * Customers
     * Return a customers tags
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-tags?mode=ShowAll
     */
    "v3/customers/{id}/tags": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
      }
      output: Tag[]
    }

    /**
     * Customers
     * Return a customers contact preferences
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-agreed-statements?mode=ShowAll
     */
    "v3/customers/{id}/agreed-statements": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        text?: string
        agreed?: boolean
      }
      output: DataProtectionStatement[]
    }

    /**
     * Customers
     * Returns a list of etickets for a customer
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-print-at-home-documents?mode=ShowAll
     */
    "v3/customers/{id}/print-at-home-documents": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        instance?: Instance
        stockItem?: StockItem
        id?: string
      }
      output: PrintAtHomeDocument[]
    }

    /**
     * Customers
     * Return a customer's order history
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-orders?mode=ShowAll
     */
    "v3/customers/{id}/orders": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        deliveries?: Delivery[]
        ticketSubscriptions?: OrderHistoryTicketSubscription[]
        firstTransactionDate?: Date
        firstTransactionDateUtc?: Date
        lastTransactionDate?: Date
        lastTransactionDateUtc?: Date
        totalPaymentCharges?: any
        totalDeliveryCharges?: any
        totalTransactionCharges?: any
        payments?: Payment[]
        refunds?: Payment[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        id?: string
        attributes?: any
        total?: any
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        charges?: Charge[]
      }
      output: Order[]
    }

    /**
     * Customers
     * Return a customers stored cards
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-customers-id-stored-cards_includePending?mode=ShowAll
     */
    "v3/customers/{id}/stored-cards?includePending={includePending}": {
      input: {
        /** Required */
        id: string
        /** Default value is False */
        includePending?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        address?: Address
        isDefault?: boolean
        maskedNumber?: string
        cardHolderName?: string
        expiryDate?: Date
        type?: CardType
        isPending?: boolean
      }
      output: StoredCard[]
    }

    /**
     * CustomPaymentTypes
     * Return the details of all custom payment types
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-custom-payment-types?mode=ShowAll
     */
    "v3/custom-payment-types": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        id?: string
        isRefund?: boolean
        attributeDefinitions?: AttributeDefinition[]
      }
      output: CustomPaymentType[]
    }

    /**
     * Events
     * Returns details of specific events in the system
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-events_instanceStart_from_instanceStart_to_instanceInterface_onSale_name?mode=ShowAll
     */
    "v3/events?instanceStart_from={instanceStart_from}&instanceStart_to={instanceStart_to}&instanceInterface={instanceInterface}&onSale={onSale}&name={name}&attributes={attributes}&instanceAttributes={instanceAttributes}": {
      input: {
        instanceStart_from?: Date
        instanceStart_to?: Date
        attributes?: NameValuePairOfString[]
        instanceAttributes?: NameValuePairOfString[]
        /** Default value is All */
        instanceInterface?: Interface
        onSale?: boolean
        name?: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        description?: string
        htmlDescription?: string
        duration?: number
        imageUrl?: string
        isOnSale?: boolean
        name?: string
        instanceDates?: string
        relatedEvents?: Event[]
        relatedStockItems?: StockItem[]
        instances?: Instance[]
        thumbnailUrl?: string
        webEventId?: string
        id?: string
        attributes?: any
        firstInstanceDateTime?: Date
        firstInstanceDateTimeUtc?: Date
        lastInstanceDateTime?: Date
        lastInstanceDateTimeUtc?: Date
        altText?: string
      }
      output: Event[]
    }

    /**
     * Events
     * Returns the details of a specific event
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-events-id?mode=ShowAll
     */
    "v3/events/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        description?: string
        htmlDescription?: string
        duration?: number
        imageUrl?: string
        isOnSale?: boolean
        name?: string
        instanceDates?: string
        relatedEvents?: Event[]
        relatedStockItems?: StockItem[]
        instances?: Instance[]
        thumbnailUrl?: string
        webEventId?: string
        id?: string
        attributes?: any
        firstInstanceDateTime?: Date
        firstInstanceDateTimeUtc?: Date
        lastInstanceDateTime?: Date
        lastInstanceDateTimeUtc?: Date
        altText?: string
      }
      output: Event
    }

    /**
     * Events
     * Returns a list of instances for a specific event
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-events-id-instances_start_from_start_to_interface?mode=ShowAll
     */
    "v3/events/{id}/instances?start_from={start_from}&start_to={start_to}&interface={interface}&attributes={attributes}": {
      input: {
        /** Required */
        id: string
        start_from?: Date
        start_to?: Date
        attributes?: NameValuePairOfString[]
        /** Default value is All */
        interface?: Interface
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isOnSale?: boolean
        planId?: string
        priceList?: PriceList
        event?: Event
        start?: Date
        startUtc?: Date
        startSellingAtWeb?: Date
        startSellingAtWebUtc?: Date
        startSellingAtAgents?: Date
        startSellingAtAgentsUtc?: Date
        startSellingAtFundraising?: Date
        startSellingAtFundraisingUtc?: Date
        startSellingAtSales?: Date
        startSellingAtSalesUtc?: Date
        stopSellingAtWeb?: Date
        stopSellingAtWebUtc?: Date
        stopSellingAtAgents?: Date
        stopSellingAtAgentsUtc?: Date
        stopSellingAtFundraising?: Date
        stopSellingAtFundraisingUtc?: Date
        stopSellingAtSales?: Date
        stopSellingAtSalesUtc?: Date
        webInstanceId?: string
        cancelled?: boolean
        id?: string
        attributes?: any
        hasBestAvailableOverlay?: boolean
      }
      output: Instance[]
    }

    /**
     * Events
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-events-id-availability_start_from_start_to?mode=ShowAll
     */
    "v3/events/{id}/availability?start_from={start_from}&start_to={start_to}": {
      input: {
        /** Required */
        id: string
        start_from?: Date
        start_to?: Date
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        data?: EventInstanceAvailabilityDto[]
      }
      output: EventAvailabilityDto
    }

    /**
     * Funds
     * Returns a list of available funds that can be donated too
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-funds?mode=ShowAll
     */
    "v3/funds?attributes={attributes}": {
      input: {
        attributes?: NameValuePairOfString[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        description?: string
        web?: boolean
        code?: string
        defaultDonationAmount?: any
        isActive?: boolean
      }
      output: Fund[]
    }

    /**
     * Instances
     * Returns a list of specific instances
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances_startFrom_startTo_interface_eventName?mode=ShowAll
     */
    "v3/instances?startFrom={startFrom}&startTo={startTo}&interface={interface}&eventName={eventName}&attributes={attributes}&eventAttributes={eventAttributes}": {
      input: {
        startFrom?: Date
        startTo?: Date
        attributes?: NameValuePairOfString[]
        /** Default value is All */
        interface?: Interface
        eventAttributes?: NameValuePairOfString[]
        eventName?: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isOnSale?: boolean
        planId?: string
        priceList?: PriceList
        event?: Event
        start?: Date
        startUtc?: Date
        startSellingAtWeb?: Date
        startSellingAtWebUtc?: Date
        startSellingAtAgents?: Date
        startSellingAtAgentsUtc?: Date
        startSellingAtFundraising?: Date
        startSellingAtFundraisingUtc?: Date
        startSellingAtSales?: Date
        startSellingAtSalesUtc?: Date
        stopSellingAtWeb?: Date
        stopSellingAtWebUtc?: Date
        stopSellingAtAgents?: Date
        stopSellingAtAgentsUtc?: Date
        stopSellingAtFundraising?: Date
        stopSellingAtFundraisingUtc?: Date
        stopSellingAtSales?: Date
        stopSellingAtSalesUtc?: Date
        webInstanceId?: string
        cancelled?: boolean
        id?: string
        attributes?: any
        hasBestAvailableOverlay?: boolean
      }
      output: Instance[]
    }

    /**
     * Instances
     * Returns the details of a specific instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id?mode=ShowAll
     */
    "v3/instances/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        isOnSale?: boolean
        planId?: string
        priceList?: PriceList
        event?: Event
        start?: Date
        startUtc?: Date
        startSellingAtWeb?: Date
        startSellingAtWebUtc?: Date
        startSellingAtAgents?: Date
        startSellingAtAgentsUtc?: Date
        startSellingAtFundraising?: Date
        startSellingAtFundraisingUtc?: Date
        startSellingAtSales?: Date
        startSellingAtSalesUtc?: Date
        stopSellingAtWeb?: Date
        stopSellingAtWebUtc?: Date
        stopSellingAtAgents?: Date
        stopSellingAtAgentsUtc?: Date
        stopSellingAtFundraising?: Date
        stopSellingAtFundraisingUtc?: Date
        stopSellingAtSales?: Date
        stopSellingAtSalesUtc?: Date
        webInstanceId?: string
        cancelled?: boolean
        id?: string
        attributes?: any
        hasBestAvailableOverlay?: boolean
      }
      output: Instance
    }

    /**
     * Instances
     * Returns the current plan information for an instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-plan?mode=ShowAll
     */
    "v3/instances/{id}/plan": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        type?: SeatingPlanType
        name?: string
        venue?: Venue
        layoutOverlay?: ApiLayoutOverlay
        backgroundImageUrl?: string
        x?: number
        y?: number
        areas?: Plan[]
        seats?: Seat[]
        capacity?: number
        maxZoomWeb?: any
        maxZoomMobile?: any
        vertices?: SeatingPlanVertices
        id?: string
      }
      output: Plan
    }

    /**
     * Instances
     * Returns an overview of the current availability status for an instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-status_includeLockInformation_includeChildPlans?mode=ShowAll
     */
    "v3/instances/{id}/status?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}": {
      input: {
        /** Required */
        id: string
        /** If true, includes the lock information for seats available to the current user — Default value is False */
        includeLockInformation?: boolean
        /** Default value is False */
        includeChildPlans?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        available?: number
        wheelchairAvailable?: number
        specialAvailable?: number
        capacity?: number
        bands?: Band[]
        inBasket?: number
        inOtherBasket?: number
        locked?: number
        reserved?: number
        sold?: number
        scanned?: number
        masked?: number
        unavailable?: number
        lockInformation?: LockedSeatQuantity[]
        lockInfoAvailable?: LockedSeatQuantity[]
        lockInfoSpecialAvailable?: LockedSeatQuantity[]
      }
      output: InstancePlanStatus
    }

    /**
     * Instances
     * Returns a detailed view of the availability of each seat for an instance. See here for a description of the response format.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-status-detail_includeLockInformation_includeChildPlans?mode=ShowAll
     */
    "v3/instances/{id}/status/detail?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}": {
      input: {
        /** Required */
        id: string
        /** If true, includes the lock information for seats available to the current user — Default value is false */
        includeLockInformation?: string
        /** Default value is False */
        includeChildPlans?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: unknown
    }

    /**
     * Instances
     * Returns a detailed view of the availability of each seat for a specific area for an instance. See here for a description of the response format.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-status-areas-areaId-detail_includeLockInformation_includeChildPlans?mode=ShowAll
     */
    "v3/instances/{id}/status/areas/{areaId}/detail?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}": {
      input: {
        /** Required */
        id: string
        /** Required */
        areaId: string
        /** If true, includes the lock information for seats available to the current user — Default value is false */
        includeLockInformation?: string
        /** Default value is False */
        includeChildPlans?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: unknown
    }

    /**
     * Instances
     * Returns an overview of the current availability status of a specific area for an instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-status-areas-areaId_includeLockInformation_includeChildPlans?mode=ShowAll
     */
    "v3/instances/{id}/status/areas/{areaId}?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}": {
      input: {
        /** Required */
        id: string
        /** Required */
        areaId: string
        /** If true, includes the lock information for seats available to the current user — Default value is False */
        includeLockInformation?: boolean
        /** Default value is False */
        includeChildPlans?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        available?: number
        wheelchairAvailable?: number
        specialAvailable?: number
        capacity?: number
        bands?: Band[]
        inBasket?: number
        inOtherBasket?: number
        locked?: number
        reserved?: number
        sold?: number
        scanned?: number
        masked?: number
        unavailable?: number
        lockInformation?: LockedSeatQuantity[]
        lockInfoAvailable?: LockedSeatQuantity[]
        lockInfoSpecialAvailable?: LockedSeatQuantity[]
      }
      output: InstancePlanStatus
    }

    /**
     * Instances
     * Returns an overview of the current availability status of a number of specific areas for an instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-status-areas_includeLockInformation_includeChildPlans?mode=ShowAll
     */
    "v3/instances/{id}/status/areas?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}&areaIds={areaIds}": {
      input: {
        /** Required */
        id: string
        /** Required */
        areaIds: string[]
        /** If true, includes the lock information for seats available to the current user — Default value is False */
        includeLockInformation?: boolean
        /** Default value is False */
        includeChildPlans?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        available?: number
        wheelchairAvailable?: number
        specialAvailable?: number
        capacity?: number
        bands?: Band[]
        inBasket?: number
        inOtherBasket?: number
        locked?: number
        reserved?: number
        sold?: number
        scanned?: number
        masked?: number
        unavailable?: number
        lockInformation?: LockedSeatQuantity[]
        lockInfoAvailable?: LockedSeatQuantity[]
        lockInfoSpecialAvailable?: LockedSeatQuantity[]
      }
      output: InstancePlanStatus
    }

    /**
     * Instances
     * Returns the current best available seats for an instance for the number specified
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-best-available_quantity?mode=ShowAll
     */
    "v3/instances/{id}/best-available?quantity={quantity}&areaIds={areaIds}&bandIds={bandIds}": {
      input: {
        /** Required */
        id: string
        /** Required */
        quantity: number
        areaIds?: string[]
        bandIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        row?: string
        number?: number
        previousSeatId?: number
        previousIsAcrossAisle?: boolean
        nextIsAcrossAisle?: boolean
        nextSeatId?: number
        x?: number
        y?: number
        tabOrder?: number
        attributes?: any
      }
      output: Seat[]
    }

    /**
     * Instances
     * Returns the current price list in use for an instance
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-instances-id-price-list_includeTicketCommission?mode=ShowAll
     */
    "v3/instances/{id}/price-list?includeTicketCommission={includeTicketCommission}": {
      input: {
        /** Required */
        id: string
        /** When true and the system is configured for non-itemised commission (PriceListsIncludeCommission = true), returns prices with commission included in the amount. Defaults to false. — Default value is False */
        includeTicketCommission?: boolean
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        prices?: Price[]
        id?: string
      }
      output: PriceList
    }

    /**
     * Memberships
     * Return a list of available Memberships
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-memberships?mode=ShowAll
     */
    "v3/memberships": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        description?: string
        htmlDescription?: string
        id?: string
        imageUrl?: string
        name?: string
        thumbnailUrl?: string
        attributes?: any
        /** For Variable Memberships ‘Price’ will be ‘Min Price’. */
        price?: any
        renewalPrice?: any
        /** For Variable Memberships, only available when IsVariable is true. */
        maxPrice?: any
        altText?: string
        isGiftable?: boolean
        isVariable?: boolean
        canAutoRenew?: boolean
        /** Expressed as structured data where ‘expiryType’ defines how data should be displayed. WeeksDays = Membership duration is {weeks} weeks, {days} days. YearsMonthsDays = Membership duration is {years} years, {months} months, {days} days. Fixed = Membership expires on {days} day of {month}. OpenEnded = does not expire. */
        membershipPeriod?: MembershipPeriod
      }
      output: Membership[]
    }

    /**
     * Offers
     * Return a list of available offers
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-offers_instanceStart_from_instanceStart_to_eventName_promoCode?mode=ShowAll
     */
    "v3/offers?instanceStart_from={instanceStart_from}&instanceStart_to={instanceStart_to}&eventName={eventName}&promoCode={promoCode}&eventAttributes={eventAttributes}&instanceAttributes={instanceAttributes}": {
      input: {
        instanceStart_from?: Date
        instanceStart_to?: Date
        eventAttributes?: NameValuePairOfString[]
        eventName?: string
        promoCode?: string
        instanceAttributes?: NameValuePairOfString[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        betterOffer?: Offer
        description?: string
        endDate?: Date
        htmlDescription?: string
        imageUrl?: string
        name?: string
        promoCodes?: string[]
        requiresPromoCode?: boolean
        startDate?: Date
        thumbnailUrl?: string
        type?: OfferType
        interfaces?: Interface[]
        discountedPrices?: PriceList
        status?: OfferStatus
        multibuyMinimumEvents?: number
        xForYX?: number
        xForYY?: number
        packageItems?: any
        statusString?: string
        id?: string
        attributes?: any
      }
      output: Offer[]
    }

    /**
     * Offers
     * Return a list of all offers on the system
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-offers-all_instanceStart_from_instanceStart_to_eventName_promoCode_interface_customerId?mode=ShowAll
     */
    "v3/offers/all?instanceStart_from={instanceStart_from}&instanceStart_to={instanceStart_to}&eventName={eventName}&promoCode={promoCode}&interface={interface}&customerId={customerId}&eventAttributes={eventAttributes}&instanceAttributes={instanceAttributes}": {
      input: {
        instanceStart_from?: Date
        instanceStart_to?: Date
        eventAttributes?: NameValuePairOfString[]
        eventName?: string
        promoCode?: string
        /** Default value is All */
        interface?: Interface
        instanceAttributes?: NameValuePairOfString[]
        customerId?: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        betterOffer?: Offer
        description?: string
        endDate?: Date
        htmlDescription?: string
        imageUrl?: string
        name?: string
        promoCodes?: string[]
        requiresPromoCode?: boolean
        startDate?: Date
        thumbnailUrl?: string
        type?: OfferType
        interfaces?: Interface[]
        discountedPrices?: PriceList
        status?: OfferStatus
        multibuyMinimumEvents?: number
        xForYX?: number
        xForYY?: number
        packageItems?: any
        statusString?: string
        id?: string
        attributes?: any
      }
      output: Offer[]
    }

    /**
     * Orders
     * Return a filtered list of orders
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-orders_confirmedOn_customerAddressLine1_customerAddressPostTown_customerAddressPostCode_customerAddressTown_customerBirthDate_customerEmailAddress_customerFriendlyId_individualId_customerId_noCustomer_customerName_customerPhoneNumber_DateFrom_DateTo_friendlyId_seatName_attributeValues?mode=ShowAll
     */
    "v3/orders?confirmedOn={confirmedOn}&customerAddressLine1={customerAddressLine1}&customerAddressPostTown={customerAddressPostTown}&customerAddressPostCode={customerAddressPostCode}&customerAddressTown={customerAddressTown}&customerBirthDate={customerBirthDate}&customerEmailAddress={customerEmailAddress}&customerFriendlyId={customerFriendlyId}&individualId={individualId}&customerId={customerId}&noCustomer={noCustomer}&customerName={customerName}&customerPhoneNumber={customerPhoneNumber}&DateFrom={DateFrom}&DateTo={DateTo}&friendlyId={friendlyId}&seatName={seatName}&attributeValues={attributeValues}": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Orders
     * Return the details of a specific order
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-orders-id?mode=ShowAll
     */
    "v3/orders/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        deliveries?: Delivery[]
        ticketSubscriptions?: OrderHistoryTicketSubscription[]
        firstTransactionDate?: Date
        firstTransactionDateUtc?: Date
        lastTransactionDate?: Date
        lastTransactionDateUtc?: Date
        totalPaymentCharges?: any
        totalDeliveryCharges?: any
        totalTransactionCharges?: any
        payments?: Payment[]
        refunds?: Payment[]
        printAtHomeDocuments?: PrintAtHomeDocument[]
        id?: string
        attributes?: any
        total?: any
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        charges?: Charge[]
      }
      output: Order
    }

    /**
     * Plans
     * Returns a specific seating plan
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-plans-id?mode=ShowAll
     */
    "v3/plans/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        type?: SeatingPlanType
        name?: string
        venue?: Venue
        layoutOverlay?: ApiLayoutOverlay
        backgroundImageUrl?: string
        x?: number
        y?: number
        areas?: Plan[]
        seats?: Seat[]
        capacity?: number
        maxZoomWeb?: any
        maxZoomMobile?: any
        vertices?: SeatingPlanVertices
        id?: string
      }
      output: Plan
    }

    /**
     * Plans
     * Returns an overview of the seating plan (like the seating plan call but without all the individual seat information)
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-plans-id-overview?mode=ShowAll
     */
    "v3/plans/{id}/overview": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        capacity?: number
        backgroundImageUrl?: string
        x?: number
        y?: number
        maxZoomWeb?: any
        maxZoomMobile?: any
        verticesJson?: string
        type?: SeatingPlanType
        vertices?: SeatingPlanVertices
        venue?: VenueOverview
        areas?: PlanOverview[]
      }
      output: PlanOverview
    }

    /**
     * Plans
     * Returns a list of seating plans and seat information for each
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-plans?mode=ShowAll
     */
    "v3/plans": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        type?: SeatingPlanType
        name?: string
        venue?: Venue
        layoutOverlay?: ApiLayoutOverlay
        backgroundImageUrl?: string
        x?: number
        y?: number
        areas?: Plan[]
        seats?: Seat[]
        capacity?: number
        maxZoomWeb?: any
        maxZoomMobile?: any
        vertices?: SeatingPlanVertices
        id?: string
      }
      output: Plan[]
    }

    /**
     * PostcodeLookup
     * Returns address information for a specific postcode id
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-postcode-lookup-postcodeLookupId?mode=ShowAll
     */
    "v3/postcode-lookup/{postcodeLookupId}": {
      input: {
        /** Required */
        postcodeLookupId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        country?: Country
        administrativeDivision?: AdministrativeDivision
        name?: string
        line1?: string
        line2?: string
        line3?: string
        line4?: string
        line5?: string
        postcode?: string
        town?: string
      }
      output: Address
    }

    /**
     * PostcodeLookup
     * Returns a list of addresses and ids for a given postcode
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-postcode-lookup_postcode?mode=ShowAll
     */
    "v3/postcode-lookup?postcode={postcode}": {
      input: {
        /** Required */
        postcode: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        /** A summary of the address */
        description?: string
        /** The postcode lookup id for the address. Use this to get the full address. */
        id?: string
      }
      output: PostcodeLookupResult[]
    }

    /**
     * PrintAtHomeDocuments
     * Returns a specific e-ticket from the current user
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-print-at-home-documents-id?mode=ShowAll
     */
    "v3/print-at-home-documents/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * QueueStatus
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-queue-status-eventId?mode=ShowAll
     */
    "queue-status/{eventId}": {
      input: {
        /** Required */
        eventId: number
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Statements
     * Returns a list of Contact Preferences
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-statements?mode=ShowAll
     */
    "v3/statements": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        text?: string
        agreed?: boolean
      }
      output: DataProtectionStatement[]
    }

    /**
     * StockItem
     * Returns a list of Merchandise items
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-stock-items?mode=ShowAll
     */
    "v3/stock-items": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        description?: string
        htmlDescription?: string
        imageUrl?: string
        name?: string
        postageAndPacking?: any
        price?: any
        stockLevel?: number
        thumbnailUrl?: string
        id?: string
        attributes?: any
      }
      output: StockItem[]
    }

    /**
     * SupportedBarcodeEncodings
     * Returns a list of supported barcode types
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-supported-barcode-encodings?mode=ShowAll
     */
    "v3/supported-barcode-encodings": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * System
     * Return system configuration information including currency, culture, and isolated seat mode
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-system-config?mode=ShowAll
     */
    "v3/system/config": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        currency?: string
        currencyCharacter?: string
        culture?: string
        isolatedSeatMode?: string
      }
      output: Config
    }

    /**
     * TagGroups
     * Returns a list of Tags and the Tag groups they sit under
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-tag-groups?mode=ShowAll
     */
    "v3/tag-groups": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
        description?: string
        web?: boolean
        tags?: Tag[]
      }
      output: TagGroup[]
    }

    /**
     * Tags
     * Returns a list of Tags
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-tags?mode=ShowAll
     */
    "v3/tags": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
      }
      output: Tag[]
    }

    /**
     * TicketSubscriptionStructures
     * Return a list of ticket subscriptions that can be purchased
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-ticket-subscription-structures?mode=ShowAll
     */
    "v3/ticket-subscription-structures": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        description?: string
        onSaleWebChannel?: Date
        onSaleWebChannelUtc?: Date
        offSaleWebChannel?: Date
        offSaleWebChannelUtc?: Date
        pricingSets?: PricingSet[]
      }
      output: TicketSubscriptionStructure[]
    }

    /**
     * TicketTypes
     * Returns a list of ticket types
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-ticket-types?mode=ShowAll
     */
    "v3/ticket-types": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        id?: string
        attributes?: any
      }
      output: TicketType[]
    }

    /**
     * TributeTypes
     * Returns a list of tribute types to be used when adding a donation
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-tribute-types?mode=ShowAll
     */
    "v3/tribute-types": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        name?: string
      }
      output: TributeType[]
    }

    /**
     * Venues
     * Returns a list of Venues
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-venues?mode=ShowAll
     */
    "v3/venues": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        address?: string
        id?: string
        attributes?: any
      }
      output: Venue[]
    }

    /**
     * Venues
     * Returns a specific Venue using a Venue id
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/GET-v3-venues-id?mode=ShowAll
     */
    "v3/venues/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        name?: string
        address?: string
        id?: string
        attributes?: any
      }
      output: Venue
    }
  }

  export interface API_POST {
    /**
     * Basket
     * Clears the contents of the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-clear?mode=ShowAll
     */
    "v3/basket/clear": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        multibuyOffers?: MultibuyOffer[]
        totalDiscount?: any
        merchandiseDeliveryOptions?: DeliveryOption[]
        defaultCardPaymentFee?: any
        potentialOffers?: Offer[]
        ticketDeliveryOptions?: DeliveryOption[]
        promoCode?: string
        id?: string
        basketId?: string
        merchandiseDelivery?: Delivery
        transactionCommission?: BasketCommission
        offers?: Offer[]
        ticketDelivery?: Delivery
        ticketSubscriptions?: TicketSubscription[]
        disableWebComponents?: boolean
        /** A value representing this basket. This will change any time the basket changes, and can be used to decide whether to redraw the basket. */
        hash?: string
        agentCustomer?: AgentCustomer
        attributes?: any
        customPayments?: CustomPayment[]
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        total?: any
        charges?: Charge[]
      }
      output: Basket
    }

    /**
     * Basket
     * Confirms an order. This can only be used for zero-value orders.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-confirm?mode=ShowAll
     */
    "v3/basket/confirm": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & OrderConfirmation
      output: Order
    }

    /**
     * Basket
     * Add a donation(s) to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-donations?mode=ShowAll
     */
    "v3/basket/donations": {
      input: NewDonation[]
      output: Donation[]
    }

    /**
     * Basket
     * Add a ticket(s) to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-tickets?mode=ShowAll
     */
    "v3/basket/tickets": {
      input: NewTicket[]
      output: Ticket[]
    }

    /**
     * Basket
     * Add items of merchandise to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-merchandise?mode=ShowAll
     */
    "v3/basket/merchandise": {
      input: NewMerchandiseItem[]
      output: MerchandiseItem[]
    }

    /**
     * Basket
     * Add a gift voucher(s) to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-gift-vouchers?mode=ShowAll
     */
    "v3/basket/gift-vouchers": {
      input: NewGiftVoucher[]
      output: GiftVoucher[]
    }

    /**
     * Basket
     * Add some best-available tickets from a reserved seating plan to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-tickets-best-available?mode=ShowAll
     */
    "v3/basket/tickets/best-available": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & BestAvailableSettings
      output: Ticket[]
    }

    /**
     * Basket
     * Add a number of memberships to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-membership-subscriptions?mode=ShowAll
     */
    "v3/basket/membership-subscriptions": {
      input: NewMembershipSubscription[]
      output: MembershipSubscription[]
    }

    /**
     * Basket
     * Add a number of ticket subscriptions to the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-basket-ticket-subscriptions?mode=ShowAll
     */
    "v3/basket/ticket-subscriptions": {
      input: NewTicketSubscription[]
      output: TicketSubscription[]
    }

    /**
     * Baskets
     * Creates a new basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets?mode=ShowAll
     */
    "v3/baskets": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewBasket
      output: Basket
    }

    /**
     * Baskets
     * Clear the contents of a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-clear?mode=ShowAll
     */
    "v3/baskets/{id}/clear": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        multibuyOffers?: MultibuyOffer[]
        totalDiscount?: any
        merchandiseDeliveryOptions?: DeliveryOption[]
        defaultCardPaymentFee?: any
        potentialOffers?: Offer[]
        ticketDeliveryOptions?: DeliveryOption[]
        promoCode?: string
        id?: string
        basketId?: string
        merchandiseDelivery?: Delivery
        transactionCommission?: BasketCommission
        offers?: Offer[]
        ticketDelivery?: Delivery
        ticketSubscriptions?: TicketSubscription[]
        disableWebComponents?: boolean
        /** A value representing this basket. This will change any time the basket changes, and can be used to decide whether to redraw the basket. */
        hash?: string
        agentCustomer?: AgentCustomer
        attributes?: any
        customPayments?: CustomPayment[]
        customer?: Customer
        merchandiseItems?: MerchandiseItem[]
        donations?: Donation[]
        notes?: string
        tickets?: Ticket[]
        membershipSubscriptions?: MembershipSubscription[]
        giftVouchers?: GiftVoucher[]
        total?: any
        charges?: Charge[]
      }
      output: Basket
    }

    /**
     * Baskets
     * Confirms an order. For system-owners, this can only be used for zero-value orders. Otherwise use . For agents, this should be used for all orders
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-confirm?mode=ShowAll
     */
    "v3/baskets/{id}/confirm": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & OrderConfirmation
      output: Order
    }

    /**
     * Baskets
     * Add a single custom payment to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-custom-payments?mode=ShowAll
     */
    "v3/baskets/{id}/custom-payments": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomPayment
      output: CustomPayment[]
    }

    /**
     * Baskets
     * Add donation(s) to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-donations?mode=ShowAll
     */
    "v3/baskets/{id}/donations": {
      input: NewDonation[]
      output: Donation[]
    }

    /**
     * Baskets
     * Add a gift voucher(s) to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-gift-vouchers?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers": {
      input: NewGiftVoucher[]
      output: GiftVoucher[]
    }

    /**
     * Baskets
     * Add a number of memberships to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-membership-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions": {
      input: NewMembershipSubscription[]
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-merchandise?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise": {
      input: NewMerchandiseItem[]
      output: MerchandiseItem[]
    }

    /**
     * Baskets
     * Add a ticket(s) to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-tickets?mode=ShowAll
     */
    "v3/baskets/{id}/tickets": {
      input: NewTicket[]
      output: Ticket[]
    }

    /**
     * Baskets
     * Add some best-available tickets from a reserved seating plan to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-tickets-best-available?mode=ShowAll
     */
    "v3/baskets/{id}/tickets/best-available": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & BestAvailableSettings
      output: Ticket[]
    }

    /**
     * Baskets
     * Add a number of ticket subscriptions to a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-baskets-id-ticket-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/ticket-subscriptions": {
      input: NewTicketSubscription[]
      output: TicketSubscription[]
    }

    /**
     * Customer
     * Create a new customer record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer_domain?mode=ShowAll
     */
    "v3/customer?domain={domain}": {
      input: {
        domain?: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomer
      output: Customer
    }

    /**
     * Customer
     * Log a customer into their online account
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-authenticate?mode=ShowAll
     */
    "v3/customer/authenticate": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & Credentials
      output: Customer
    }

    /**
     * Customer
     * Allow the current user to change a password
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-change-password?mode=ShowAll
     */
    "v3/customer/change-password": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewPasswordInfo
      output: void
    }

    /**
     * Customer
     * Set a new password for the customer
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-set-password?mode=ShowAll
     */
    "v3/customer/set-password": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewPasswordFromLink
      output: void
    }

    /**
     * Customer
     * Send a customer a password reset email
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-forgot-password_emailAddress_domain?mode=ShowAll
     */
    "v3/customer/forgot-password?emailAddress={emailAddress}&domain={domain}": {
      input: {
        /** Required */
        emailAddress: string
        domain?: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Validate a customers password reset token
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-validate-password-link_token?mode=ShowAll
     */
    "v3/customer/validate-password-link?token={token}": {
      input: {
        /** Required */
        token: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      /** Spektrix responds 401 rather than false for an invalid or expired token. */
      output: boolean
    }

    /**
     * Customer
     * Log the current customer out of their online account
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-deauthenticate?mode=ShowAll
     */
    "v3/customer/deauthenticate": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Add a new address to the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-addresses?mode=ShowAll
     */
    "v3/customer/addresses": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomerAddress
      output: CustomerAddress
    }

    /**
     * Customer
     * Add a new tag to the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-tags?mode=ShowAll
     */
    "v3/customer/tags": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomerTag
      output: Tag
    }

    /**
     * Customer
     * Add a contact preference to the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-agreed-statements?mode=ShowAll
     */
    "v3/customer/agreed-statements": {
      input: NewCustomerDataProtectionStatement[]
      output: DataProtectionStatement[]
    }

    /**
     * Customers
     * Create a new customer record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers?mode=ShowAll
     */
    "v3/customers": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomer
      output: Customer
    }

    /**
     * Customers
     * Log a customer into their online account
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers-id-authenticate?mode=ShowAll
     */
    "v3/customers/{id}/authenticate": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & Credentials
      output: Customer
    }

    /**
     * Customers
     * Send a password reset email to a specific customer
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers-id-reset-password?mode=ShowAll
     */
    "v3/customers/{id}/reset-password": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Add a new address to a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers-id-addresses?mode=ShowAll
     */
    "v3/customers/{id}/addresses": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomerAddress
      output: CustomerAddress
    }

    /**
     * Customers
     * Add a new tag to a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers-id-tags?mode=ShowAll
     */
    "v3/customers/{id}/tags": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & NewCustomerTag
      output: Tag
    }

    /**
     * Customers
     * Add a contact preference to a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customers-id-agreed-statements?mode=ShowAll
     */
    "v3/customers/{id}/agreed-statements": {
      input: NewCustomerDataProtectionStatement[]
      output: DataProtectionStatement[]
    }

    /**
     * Orders
     * Send a system confirmation email for a specific order
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-orders-id-send-confirmation?mode=ShowAll
     */
    "v3/orders/{id}/send-confirmation": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }
  }

  export interface API_PUT {
    /**
     * Basket
     * Replace all merchandise items in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-basket-merchandise?mode=ShowAll
     */
    "v3/basket/merchandise": {
      input: NewMerchandiseItem[]
      output: MerchandiseItem[]
    }

    /**
     * Basket
     * Replace all gift vouchers in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-basket-gift-vouchers?mode=ShowAll
     */
    "v3/basket/gift-vouchers": {
      input: NewGiftVoucher[]
      output: GiftVoucher[]
    }

    /**
     * Basket
     * Replace all the tickets in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-basket-tickets?mode=ShowAll
     */
    "v3/basket/tickets": {
      input: NewTicket[]
      output: Ticket[]
    }

    /**
     * Basket
     * Replace all memberships in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-basket-membership-subscriptions?mode=ShowAll
     */
    "v3/basket/membership-subscriptions": {
      input: NewMembershipSubscription[]
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * Replace all gift vouchers in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-baskets-id-gift-vouchers?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers": {
      input: NewGiftVoucher[]
      output: GiftVoucher[]
    }

    /**
     * Baskets
     * Replace all memberships in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-baskets-id-membership-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions": {
      input: NewMembershipSubscription[]
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * Replace all merchandise items in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-baskets-id-merchandise?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise": {
      input: NewMerchandiseItem[]
      output: MerchandiseItem[]
    }

    /**
     * Customer
     * Replace all current addresses on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customer-addresses?mode=ShowAll
     */
    "v3/customer/addresses": {
      input: NewCustomerAddress[]
      output: CustomerAddress[]
    }

    /**
     * Customer
     * Amend an address on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customer-addresses-addressId?mode=ShowAll
     */
    "v3/customer/addresses/{addressId}": {
      input: {
        /** Required */
        addressId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & AddressPatch
      output: CustomerAddress
    }

    /**
     * Customer
     * Replace all current tags on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customer-tags?mode=ShowAll
     */
    "v3/customer/tags": {
      input: NewCustomerTag[]
      output: Tag[]
    }

    /**
     * Customer
     * Replace all contact preferences on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customer-agreed-statements?mode=ShowAll
     */
    "v3/customer/agreed-statements": {
      input: NewCustomerDataProtectionStatement[]
      output: DataProtectionStatement[]
    }

    /**
     * Customers
     * Replace all current addresses on a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customers-id-addresses?mode=ShowAll
     */
    "v3/customers/{id}/addresses": {
      input: NewCustomerAddress[]
      output: CustomerAddress[]
    }

    /**
     * Customers
     * Replace all current tags on a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customers-id-tags?mode=ShowAll
     */
    "v3/customers/{id}/tags": {
      input: NewCustomerTag[]
      output: Tag[]
    }

    /**
     * Customers
     * Replace all contact preferences on a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PUT-v3-customers-id-agreed-statements?mode=ShowAll
     */
    "v3/customers/{id}/agreed-statements": {
      input: NewCustomerDataProtectionStatement[]
      output: DataProtectionStatement[]
    }
  }

  export interface API_PATCH {
    /**
     * Basket
     * To add additional information to the basket, such as a Customer, Promocode, Attributes and Delivery Methods
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket?mode=ShowAll
     */
    "v3/basket": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & BasketPatch
      output: Basket
    }

    /**
     * Basket
     * Update the details of a specific donation in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-donations-donationId?mode=ShowAll
     */
    "v3/basket/donations/{donationId}": {
      input: {
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & DonationPatch
      output: Donation
    }

    /**
     * Basket
     * Update the details of a number of donations in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-donations?mode=ShowAll
     */
    "v3/basket/donations?donationIds={donationIds}": {
      input: {
        donationIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & DonationPatch
      output: Donation[]
    }

    /**
     * Basket
     * Update the details of a number of gift vouchers in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-gift-vouchers?mode=ShowAll
     */
    "v3/basket/gift-vouchers?giftVoucherIds={giftVoucherIds}": {
      input: {
        giftVoucherIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & GiftVoucherPatch
      output: GiftVoucher[]
    }

    /**
     * Basket
     * Update the details of a specific gift voucher in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/basket/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & GiftVoucherPatch
      output: GiftVoucher
    }

    /**
     * Basket
     * Update the detail of a specific ticket in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-tickets-ticketId?mode=ShowAll
     */
    "v3/basket/tickets/{ticketId}": {
      input: {
        /** Required */
        ticketId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & TicketPatch
      output: Ticket
    }

    /**
     * Basket
     * Update the details of a number of tickets in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-tickets?mode=ShowAll
     */
    "v3/basket/tickets?ticketIds={ticketIds}": {
      input: {
        ticketIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & TicketPatch
      output: Ticket[]
    }

    /**
     * Basket
     * Update the details of a specific merchandise item in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-merchandise-merchandiseItemId?mode=ShowAll
     */
    "v3/basket/merchandise/{merchandiseItemId}": {
      input: {
        /** Required */
        merchandiseItemId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MerchandiseItemPatch
      output: MerchandiseItem
    }

    /**
     * Basket
     * Amend the details of a specific membership in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/basket/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MembershipSubscriptionPatch
      output: MembershipSubscription
    }

    /**
     * Basket
     * Amend the details of a number of memberships in the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-basket-membership-subscriptions?mode=ShowAll
     */
    "v3/basket/membership-subscriptions?subscriptionIds={subscriptionIds}": {
      input: {
        subscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MembershipSubscriptionPatch
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * To add additional information to a basket, such as a Customer, Promocode, Attributes and Delivery Methods
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id?mode=ShowAll
     */
    "v3/baskets/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & BasketPatch
      output: Basket
    }

    /**
     * Baskets
     * Update the details of a specific donation in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-donations-donationId?mode=ShowAll
     */
    "v3/baskets/{id}/donations/{donationId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & DonationPatch
      output: Donation
    }

    /**
     * Baskets
     * Update the details of a number of donations in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-donations?mode=ShowAll
     */
    "v3/baskets/{id}/donations?donationIds={donationIds}": {
      input: {
        /** The basket id — Required */
        id: string
        /** A comma-separated list of ids to patch. If not present, this method patch all donations in the basket */
        donationIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & DonationPatch
      output: Donation[]
    }

    /**
     * Baskets
     * Update the details of a specific gift voucher in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & GiftVoucherPatch
      output: GiftVoucher
    }

    /**
     * Baskets
     * Update the details of a number of gift vouchers in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-gift-vouchers?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers?giftVoucherIds={giftVoucherIds}": {
      input: {
        /** Required */
        id: string
        giftVoucherIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & GiftVoucherPatch
      output: GiftVoucher[]
    }

    /**
     * Baskets
     * Amend the details of a specific membership in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MembershipSubscriptionPatch
      output: MembershipSubscription
    }

    /**
     * Baskets
     * Amend the details of a number of memberships in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-membership-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions?subscriptionIds={subscriptionIds}": {
      input: {
        /** Required */
        id: string
        subscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MembershipSubscriptionPatch
      output: MembershipSubscription[]
    }

    /**
     * Baskets
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-merchandise-merchandiseItemId?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise/{merchandiseItemId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        merchandiseItemId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MerchandiseItemPatch
      output: MerchandiseItem
    }

    /**
     * Baskets
     * No documentation available.
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-merchandise?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise?merchandiseItemIds={merchandiseItemIds}": {
      input: {
        /** Required */
        id: string
        merchandiseItemIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & MerchandiseItemPatch
      output: MerchandiseItem[]
    }

    /**
     * Baskets
     * Update the detail of a specific ticket in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-tickets-ticketId?mode=ShowAll
     */
    "v3/baskets/{id}/tickets/{ticketId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        ticketId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & TicketPatch
      output: Ticket
    }

    /**
     * Baskets
     * Update the details of a number of tickets in a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-baskets-id-tickets?mode=ShowAll
     */
    "v3/baskets/{id}/tickets?ticketIds={ticketIds}": {
      input: {
        /** Required */
        id: string
        ticketIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & TicketPatch
      output: Ticket[]
    }

    /**
     * Customer
     * Update details on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-customer?mode=ShowAll
     */
    "v3/customer": {
      input: {
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & CustomerPatch
      output: Customer
    }

    /**
     * Customer
     * Amend an address on the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-customer-addresses-addressId?mode=ShowAll
     */
    "v3/customer/addresses/{addressId}": {
      input: {
        /** Required */
        addressId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & AddressPatch
      output: CustomerAddress
    }

    /**
     * Customers
     * Update the details on a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-customers-id?mode=ShowAll
     */
    "v3/customers/{id}": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & CustomerPatch
      output: Customer
    }

    /**
     * Customers
     * Amend an address on a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-customers-id-addresses-addressId?mode=ShowAll
     */
    "v3/customers/{id}/addresses/{addressId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        addressId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & AddressPatch
      output: CustomerAddress
    }

    /**
     * Instances
     * Update existing prices of an instance's price list
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/PATCH-v3-instances-id-price-list?mode=ShowAll
     */
    "v3/instances/{id}/price-list": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & PriceListPatch
      output: PriceList
    }
  }

  export interface API_DELETE {
    /**
     * Basket
     * Remove a number of donations from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-donations?mode=ShowAll
     */
    "v3/basket/donations?donationIds={donationIds}": {
      input: {
        donationIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a specific donation from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-donations-donationId?mode=ShowAll
     */
    "v3/basket/donations/{donationId}": {
      input: {
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a number of merchandise items from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-merchandise?mode=ShowAll
     */
    "v3/basket/merchandise?merchandiseItemIds={merchandiseItemIds}": {
      input: {
        merchandiseItemIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a specific merchandise item from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-merchandise-merchandiseItemId?mode=ShowAll
     */
    "v3/basket/merchandise/{merchandiseItemId}": {
      input: {
        /** Required */
        merchandiseItemId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a number of gift vouchers from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-gift-vouchers?mode=ShowAll
     */
    "v3/basket/gift-vouchers?giftVoucherIds={giftVoucherIds}": {
      input: {
        giftVoucherIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a specific gift voucher from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/basket/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove specific tickets from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-tickets?mode=ShowAll
     */
    "v3/basket/tickets?ticketIds={ticketIds}": {
      input: {
        ticketIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a specific membership from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/basket/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a number of memberships from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-membership-subscriptions?mode=ShowAll
     */
    "v3/basket/membership-subscriptions?subscriptionIds={subscriptionIds}": {
      input: {
        subscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove a specific ticket subscription from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-ticket-subscriptions-ticketSubscriptionId?mode=ShowAll
     */
    "v3/basket/ticket-subscriptions/{ticketSubscriptionId}": {
      input: {
        /** Required */
        ticketSubscriptionId: any
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Basket
     * Remove multiple ticket subscriptions from the current basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-basket-ticket-subscriptions?mode=ShowAll
     */
    "v3/basket/ticket-subscriptions?ticketSubscriptionIds={ticketSubscriptionIds}": {
      input: {
        ticketSubscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a specific donation from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-donations-donationId?mode=ShowAll
     */
    "v3/baskets/{id}/donations/{donationId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        donationId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a number of donations from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-donations?mode=ShowAll
     */
    "v3/baskets/{id}/donations?donationIds={donationIds}": {
      input: {
        /** The basket id — Required */
        id: string
        /** A comma-separated list of ids to delete. If not present, this method deletes all donations from the basket */
        donationIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a number of gift vouchers from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-gift-vouchers?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers?giftVoucherIds={giftVoucherIds}": {
      input: {
        /** Required */
        id: string
        giftVoucherIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a specific gift voucher from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-gift-vouchers-giftVoucherId?mode=ShowAll
     */
    "v3/baskets/{id}/gift-vouchers/{giftVoucherId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        giftVoucherId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a specific membership from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-membership-subscriptions-subscriptionId?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions/{subscriptionId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        subscriptionId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a number of memberships from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-membership-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/membership-subscriptions?subscriptionIds={subscriptionIds}": {
      input: {
        /** Required */
        id: string
        subscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a specific merchandise item from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-merchandise-merchandiseItemId?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise/{merchandiseItemId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        merchandiseItemId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a number of merchandise items from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-merchandise?mode=ShowAll
     */
    "v3/baskets/{id}/merchandise?merchandiseItemIds={merchandiseItemIds}": {
      input: {
        /** Required */
        id: string
        merchandiseItemIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Delete a specific ticket from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-tickets-ticketId?mode=ShowAll
     */
    "v3/baskets/{id}/tickets/{ticketId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        ticketId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove specific tickets from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-tickets?mode=ShowAll
     */
    "v3/baskets/{id}/tickets?ticketIds={ticketIds}": {
      input: {
        /** Required */
        id: string
        ticketIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove a specific ticket subscription from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-ticket-subscriptions-ticketSubscriptionId?mode=ShowAll
     */
    "v3/baskets/{id}/ticket-subscriptions/{ticketSubscriptionId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        ticketSubscriptionId: any
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Baskets
     * Remove multiple ticket subscriptions from a basket
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-baskets-id-ticket-subscriptions?mode=ShowAll
     */
    "v3/baskets/{id}/ticket-subscriptions?ticketSubscriptionIds={ticketSubscriptionIds}": {
      input: {
        /** Required */
        id: string
        ticketSubscriptionIds?: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a number of addresses from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-addresses?mode=ShowAll
     */
    "v3/customer/addresses?addressIds={addressIds}": {
      input: {
        /** Required */
        addressIds: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a specific address from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-addresses-addressId?mode=ShowAll
     */
    "v3/customer/addresses/{addressId}": {
      input: {
        /** Required */
        addressId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a specific tag from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-tags-tagId?mode=ShowAll
     */
    "v3/customer/tags/{tagId}": {
      input: {
        /** Required */
        tagId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a specific contact preference from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-agreed-statements-statementId?mode=ShowAll
     */
    "v3/customer/agreed-statements/{statementId}": {
      input: {
        /** Required */
        statementId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a number of contact preferences from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-agreed-statements?mode=ShowAll
     */
    "v3/customer/agreed-statements?statementIds={statementIds}": {
      input: {
        /** Required */
        statementIds: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customer
     * Remove a specific stored card from the current customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customer-stored-cards-cardId?mode=ShowAll
     */
    "v3/customer/stored-cards/{cardId}": {
      input: {
        /** Required */
        cardId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        address?: Address
        isDefault?: boolean
        maskedNumber?: string
        cardHolderName?: string
        expiryDate?: Date
        type?: CardType
        isPending?: boolean
      }
      output: StoredCard[]
    }

    /**
     * Customers
     * Remove a number of addresses from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-addresses?mode=ShowAll
     */
    "v3/customers/{id}/addresses": {
      input: {
        /** Required */
        id: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a specific address from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-addresses-addressId?mode=ShowAll
     */
    "v3/customers/{id}/addresses/{addressId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        addressId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a number of tags from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-tags?mode=ShowAll
     */
    "v3/customers/{id}/tags?tagIds={tagIds}": {
      input: {
        /** Required */
        id: string
        /** Required */
        tagIds: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a specific tag from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-tags-tagId_id?mode=ShowAll
     */
    "v3/customers/tags/{tagId}?id={id}": {
      input: {
        /** Required */
        id: string
        /** Required */
        tagId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a specific contact preference from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-agreed-statements-statementId?mode=ShowAll
     */
    "v3/customers/{id}/agreed-statements/{statementId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        statementId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a number of contact preferences from a customers record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-agreed-statements?mode=ShowAll
     */
    "v3/customers/{id}/agreed-statements?statementIds={statementIds}": {
      input: {
        /** Required */
        id: string
        /** Required */
        statementIds: string[]
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      }
      output: void
    }

    /**
     * Customers
     * Remove a specific stored card from a customer record
     * @see https://system.spektrix.com/apitesting/api/v3/Help/Api/DELETE-v3-customers-id-stored-cards-cardId?mode=ShowAll
     */
    "v3/customers/{id}/stored-cards/{cardId}": {
      input: {
        /** Required */
        id: string
        /** Required */
        cardId: string
        /** OData-style $expand, e.g. "instances,customer" */
        expand?: string
      } & {
        id?: string
        address?: Address
        isDefault?: boolean
        maskedNumber?: string
        cardHolderName?: string
        expiryDate?: Date
        type?: CardType
        isPending?: boolean
      }
      output: StoredCard[]
    }
  }

  export const PUBLIC_ENDPOINTS = new Set<string>([
    "DELETE v3/basket/donations/{donationId}",
    "DELETE v3/basket/donations?donationIds={donationIds}",
    "DELETE v3/basket/gift-vouchers/{giftVoucherId}",
    "DELETE v3/basket/gift-vouchers?giftVoucherIds={giftVoucherIds}",
    "DELETE v3/basket/membership-subscriptions/{subscriptionId}",
    "DELETE v3/basket/membership-subscriptions?subscriptionIds={subscriptionIds}",
    "DELETE v3/basket/merchandise/{merchandiseItemId}",
    "DELETE v3/basket/merchandise?merchandiseItemIds={merchandiseItemIds}",
    "DELETE v3/basket/ticket-subscriptions/{ticketSubscriptionId}",
    "DELETE v3/basket/ticket-subscriptions?ticketSubscriptionIds={ticketSubscriptionIds}",
    "DELETE v3/basket/tickets?ticketIds={ticketIds}",
    "DELETE v3/customer/addresses/{addressId}",
    "DELETE v3/customer/addresses?addressIds={addressIds}",
    "DELETE v3/customer/agreed-statements/{statementId}",
    "DELETE v3/customer/agreed-statements?statementIds={statementIds}",
    "DELETE v3/customer/stored-cards/{cardId}",
    "DELETE v3/customer/tags/{tagId}",
    "GET queue-status/{eventId}",
    "GET v3/addresses/{postcodeLookupId}",
    "GET v3/addresses?postcode={postcode}",
    "GET v3/bands",
    "GET v3/basket",
    "GET v3/basket/donations",
    "GET v3/basket/donations-to-prompt",
    "GET v3/basket/donations/{donationId}",
    "GET v3/basket/gift-vouchers",
    "GET v3/basket/gift-vouchers/{giftVoucherId}",
    "GET v3/basket/membership-subscriptions",
    "GET v3/basket/membership-subscriptions/{subscriptionId}",
    "GET v3/basket/merchandise",
    "GET v3/basket/payment",
    "GET v3/basket/potentialDiscount?membershipId={membershipId}",
    "GET v3/basket/ticket-subscriptions",
    "GET v3/basket/ticket-subscriptions/{ticketSubscriptionId}",
    "GET v3/basket/tickets",
    "GET v3/countries",
    "GET v3/countries/{isoCode}",
    "GET v3/customer",
    "GET v3/customer/addresses",
    "GET v3/customer/agreed-statements",
    "GET v3/customer/orders",
    "GET v3/customer/print-at-home-documents",
    "GET v3/customer/stored-cards?includePending={includePending}",
    "GET v3/customer/tags",
    "GET v3/events/{id}",
    "GET v3/events/{id}/availability?start_from={start_from}&start_to={start_to}",
    "GET v3/events/{id}/instances?start_from={start_from}&start_to={start_to}&interface={interface}&attributes={attributes}",
    "GET v3/events?instanceStart_from={instanceStart_from}&instanceStart_to={instanceStart_to}&instanceInterface={instanceInterface}&onSale={onSale}&name={name}&attributes={attributes}&instanceAttributes={instanceAttributes}",
    "GET v3/funds?attributes={attributes}",
    "GET v3/instances/{id}",
    "GET v3/instances/{id}/best-available?quantity={quantity}&areaIds={areaIds}&bandIds={bandIds}",
    "GET v3/instances/{id}/plan",
    "GET v3/instances/{id}/price-list?includeTicketCommission={includeTicketCommission}",
    "GET v3/instances/{id}/status/areas/{areaId}/detail?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}",
    "GET v3/instances/{id}/status/areas/{areaId}?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}",
    "GET v3/instances/{id}/status/areas?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}&areaIds={areaIds}",
    "GET v3/instances/{id}/status/detail?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}",
    "GET v3/instances/{id}/status?includeLockInformation={includeLockInformation}&includeChildPlans={includeChildPlans}",
    "GET v3/instances?startFrom={startFrom}&startTo={startTo}&interface={interface}&eventName={eventName}&attributes={attributes}&eventAttributes={eventAttributes}",
    "GET v3/memberships",
    "GET v3/offers?instanceStart_from={instanceStart_from}&instanceStart_to={instanceStart_to}&eventName={eventName}&promoCode={promoCode}&eventAttributes={eventAttributes}&instanceAttributes={instanceAttributes}",
    "GET v3/plans",
    "GET v3/plans/{id}",
    "GET v3/plans/{id}/overview",
    "GET v3/postcode-lookup/{postcodeLookupId}",
    "GET v3/postcode-lookup?postcode={postcode}",
    "GET v3/print-at-home-documents/{id}",
    "GET v3/statements",
    "GET v3/stock-items",
    "GET v3/supported-barcode-encodings",
    "GET v3/system/config",
    "GET v3/tag-groups",
    "GET v3/ticket-subscription-structures",
    "GET v3/ticket-types",
    "GET v3/tribute-types",
    "GET v3/venues",
    "GET v3/venues/{id}",
    "PATCH v3/basket",
    "PATCH v3/basket/donations/{donationId}",
    "PATCH v3/basket/donations?donationIds={donationIds}",
    "PATCH v3/basket/gift-vouchers/{giftVoucherId}",
    "PATCH v3/basket/gift-vouchers?giftVoucherIds={giftVoucherIds}",
    "PATCH v3/basket/membership-subscriptions/{subscriptionId}",
    "PATCH v3/basket/membership-subscriptions?subscriptionIds={subscriptionIds}",
    "PATCH v3/basket/merchandise/{merchandiseItemId}",
    "PATCH v3/basket/tickets/{ticketId}",
    "PATCH v3/basket/tickets?ticketIds={ticketIds}",
    "PATCH v3/customer",
    "PATCH v3/customer/addresses/{addressId}",
    "POST v3/basket/clear",
    "POST v3/basket/confirm",
    "POST v3/basket/donations",
    "POST v3/basket/gift-vouchers",
    "POST v3/basket/membership-subscriptions",
    "POST v3/basket/merchandise",
    "POST v3/basket/ticket-subscriptions",
    "POST v3/basket/tickets",
    "POST v3/basket/tickets/best-available",
    "POST v3/customer/addresses",
    "POST v3/customer/agreed-statements",
    "POST v3/customer/authenticate",
    "POST v3/customer/change-password",
    "POST v3/customer/deauthenticate",
    "POST v3/customer/forgot-password?emailAddress={emailAddress}&domain={domain}",
    "POST v3/customer/set-password",
    "POST v3/customer/tags",
    "POST v3/customer/validate-password-link?token={token}",
    "POST v3/customer?domain={domain}",
    "PUT v3/basket/gift-vouchers",
    "PUT v3/basket/membership-subscriptions",
    "PUT v3/basket/merchandise",
    "PUT v3/basket/tickets",
    "PUT v3/customer/addresses",
    "PUT v3/customer/addresses/{addressId}",
    "PUT v3/customer/agreed-statements",
    "PUT v3/customer/tags",
  ])

  export type ClientOptions = {
    baseUrl: string
    credentials?: RequestCredentials
    user?: string
    key?: string
  }

  export type RequestOptions = { signal?: AbortSignal }

  export class HttpError extends Error {
    readonly instanceLimits: { instanceId: string; maxTickets: number }[]

    constructor(
      public readonly status: number,
      data: { message?: string; instanceLimits?: { instanceId: string; maxTickets: number }[] } = {},
    ) {
      super(data.message || "The ticketing system could not complete this request. Please try again.")
      this.name = "SpektrixHttpError"
      this.instanceLimits = data.instanceLimits ?? []
    }
  }

  // The same typed client serves anonymous browser requests and signed server requests.
  // Credentials are supplied explicitly; this module never reads environment variables.
  export function createClient(config: ClientOptions) {
    async function send(method: string, endpoint: string, input: any, options?: RequestOptions) {
      const response = await request(config, method, endpoint, input, options)
      return response.status === 204 ? undefined : response.json()
    }

    return {
      request(method: string, endpoint: string, input?: any, options?: RequestOptions) {
        return send(method, endpoint, input, options)
      },
      GET<E extends keyof API_GET>(
        endpoint: E,
        input: API_GET[E]["input"],
        options?: RequestOptions,
      ): Promise<API_GET[E]["output"]> {
        return send("GET", endpoint, input, options)
      },
      POST<E extends keyof API_POST>(
        endpoint: E,
        input: API_POST[E]["input"],
        options?: RequestOptions,
      ): Promise<API_POST[E]["output"]> {
        return send("POST", endpoint, input, options)
      },
      PUT<E extends keyof API_PUT>(
        endpoint: E,
        input: API_PUT[E]["input"],
        options?: RequestOptions,
      ): Promise<API_PUT[E]["output"]> {
        return send("PUT", endpoint, input, options)
      },
      PATCH<E extends keyof API_PATCH>(
        endpoint: E,
        input: API_PATCH[E]["input"],
        options?: RequestOptions,
      ): Promise<API_PATCH[E]["output"]> {
        return send("PATCH", endpoint, input, options)
      },
      DELETE<E extends keyof API_DELETE>(
        endpoint: E,
        input: API_DELETE[E]["input"],
        options?: RequestOptions,
      ): Promise<API_DELETE[E]["output"]> {
        return send("DELETE", endpoint, input, options)
      },
    }
  }

  export type Client = ReturnType<typeof createClient>

  /**
   * Endpoints annotated with trailing `,` inside a template placeholder, e.g.
   * `v3/basket/donations?donationIds={donationIds,}`, should have array values
   * serialized as a single comma-joined string, not as repeated query keys.
   * Populate as you confirm the correct format per endpoint.
   */
  export const ARRAY_COMMA_JOIN = new Set<string>([])

  const textEncoder = new TextEncoder()

  function base64ToBytes(b64: string): Uint8Array {
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
    return out
  }

  function bytesToBase64(bytes: Uint8Array): string {
    let bin = ""
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!)
    return btoa(bin)
  }

  async function hmacSha1Base64(keyBytes: Uint8Array, message: string): Promise<string> {
    const key = await crypto.subtle.importKey("raw", keyBytes as BufferSource, { name: "HMAC", hash: "SHA-1" }, false, [
      "sign",
    ])
    const sig = await crypto.subtle.sign("HMAC", key, textEncoder.encode(message) as BufferSource)
    return bytesToBase64(new Uint8Array(sig))
  }

  function md5(input: Uint8Array): Uint8Array {
    const blocks = Math.ceil((input.length + 9) / 64)
    const buf = new Uint8Array(blocks * 64)
    buf.set(input)
    buf[input.length] = 0x80
    const view = new DataView(buf.buffer)
    const bits = BigInt(input.length) * 8n
    view.setUint32(blocks * 64 - 8, Number(bits & 0xffffffffn), true)
    view.setUint32(blocks * 64 - 4, Number((bits >> 32n) & 0xffffffffn), true)
    let a0 = 0x67452301,
      b0 = 0xefcdab89,
      c0 = 0x98badcfe,
      d0 = 0x10325476
    const K = [
      0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501, 0x698098d8,
      0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821, 0xf61e2562, 0xc040b340,
      0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8, 0x21e1cde6, 0xc33707d6, 0xf4d50d87,
      0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a, 0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c,
      0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70, 0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039,
      0xe6db99e5, 0x1fa27cf8, 0xc4ac5665, 0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92,
      0xffeff47d, 0x85845dd1, 0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb,
      0xeb86d391,
    ]
    const S = [
      7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
      20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6,
      10, 15, 21,
    ]
    for (let i = 0; i < blocks; i++) {
      const M = new Array<number>(16)
      for (let j = 0; j < 16; j++) M[j] = view.getUint32(i * 64 + j * 4, true)
      let A = a0,
        B = b0,
        C = c0,
        D = d0
      for (let k = 0; k < 64; k++) {
        let F: number, g: number
        if (k < 16) {
          F = (B & C) | (~B & D)
          g = k
        } else if (k < 32) {
          F = (D & B) | (~D & C)
          g = (5 * k + 1) & 15
        } else if (k < 48) {
          F = B ^ C ^ D
          g = (3 * k + 5) & 15
        } else {
          F = C ^ (B | ~D)
          g = (7 * k) & 15
        }
        F = (F + A + K[k]! + M[g]!) | 0
        A = D
        D = C
        C = B
        B = (B + (((F << S[k]!) | (F >>> (32 - S[k]!))) >>> 0)) | 0
      }
      a0 = (a0 + A) | 0
      b0 = (b0 + B) | 0
      c0 = (c0 + C) | 0
      d0 = (d0 + D) | 0
    }
    const out = new Uint8Array(16)
    const outView = new DataView(out.buffer)
    outView.setUint32(0, a0, true)
    outView.setUint32(4, b0, true)
    outView.setUint32(8, c0, true)
    outView.setUint32(12, d0, true)
    return out
  }

  function formatParamValue(v: unknown): string {
    if (v instanceof Date) return v.toISOString()
    return String(v)
  }

  function appendQueryValue(params: URLSearchParams, name: string, value: unknown, commaJoin: boolean): void {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      if (commaJoin) {
        params.append(name, value.map(formatParamValue).join(","))
      } else {
        for (const v of value) params.append(name, formatParamValue(v))
      }
    } else {
      params.append(name, formatParamValue(value))
    }
  }

  async function request(
    config: ClientOptions,
    method: string,
    endpoint: string,
    input: Record<string, any> | any[] | undefined,
    options: RequestOptions = {},
  ): Promise<Response> {
    const baseUrl = config.baseUrl.replace(/\/$/, "")
    const { user, key } = config

    // A placeholder like {name} or {name,}. The trailing "," marks a
    // comma-joined array serialization hint.
    const placeholderRe = /\{([^}]+?)(,?)\}/g

    const templateParamNames = new Map<string, { commaJoin: boolean }>()
    const pathParamNames = new Set<string>()
    const queryParamNames = new Set<string>()

    const splitIdx = endpoint.indexOf("?")
    const pathTemplate = splitIdx === -1 ? endpoint : endpoint.slice(0, splitIdx)
    const queryTemplate = splitIdx === -1 ? "" : endpoint.slice(splitIdx + 1)

    let match: RegExpExecArray | null
    while ((match = placeholderRe.exec(pathTemplate)) !== null) {
      pathParamNames.add(match[1]!)
      templateParamNames.set(match[1]!, { commaJoin: match[2] === "," })
    }
    placeholderRe.lastIndex = 0
    while ((match = placeholderRe.exec(queryTemplate)) !== null) {
      queryParamNames.add(match[1]!)
      templateParamNames.set(match[1]!, { commaJoin: match[2] === "," })
    }

    const inputIsArray = Array.isArray(input)
    const inputObj = (inputIsArray ? {} : (input ?? {})) as Record<string, any>

    const resolvedPath = pathTemplate.replace(placeholderRe, (_: string, name: string) => {
      const v = inputObj[name]
      if (v === undefined || v === null) throw new Error(`Missing required path parameter "${name}"`)
      return encodeURIComponent(formatParamValue(v))
    })

    const searchParams = new URLSearchParams(queryTemplate)
    for (const [name, value] of [...searchParams]) {
      if (value.includes("{")) searchParams.delete(name)
    }
    let body: any = inputIsArray ? input : {}

    if (!inputIsArray) {
      const { expand, ...rest } = inputObj
      if (expand !== undefined && expand !== null && expand !== "") {
        searchParams.append("$expand", String(expand))
      }
      const overrideKey = `${method} ${endpoint}`
      const forceCommaAll = ARRAY_COMMA_JOIN.has(overrideKey)
      for (const [k, v] of Object.entries(rest)) {
        if (v === undefined) continue
        if (pathParamNames.has(k) && !queryParamNames.has(k)) continue
        const hint = templateParamNames.get(k)
        const commaJoin =
          (hint?.commaJoin ?? false) || forceCommaAll || ARRAY_COMMA_JOIN.has(`${method} ${endpoint}#${k}`)
        if (queryParamNames.has(k) || method === "GET") {
          appendQueryValue(searchParams, k, v, commaJoin)
        } else {
          body[k] = v
        }
      }
    }

    const qs = searchParams.toString()
    const pathAndQuery = resolvedPath + (qs ? `?${qs}` : "")
    const requestUrl = `${baseUrl}/${pathAndQuery.replace(/^\//, "")}`

    const apiBody = method === "GET" ? undefined : JSON.stringify(body)
    const headers: Record<string, string> = { Accept: "application/json" }
    if (apiBody !== undefined) headers["Content-Type"] = "application/json"

    if (key && user) {
      const date = new Date().toUTCString()
      let stringToSign = `${method}\n${requestUrl}\n${date}`
      if (apiBody !== undefined) {
        stringToSign += `\n${bytesToBase64(md5(textEncoder.encode(apiBody)))}`
      }
      const signature = await hmacSha1Base64(base64ToBytes(key), stringToSign)
      headers.Date = date
      headers.Authorization = `SpektrixAPI3 ${user}:${signature}`
    }

    const timeout = AbortSignal.timeout(30_000)
    let response: Response
    try {
      response = await fetch(requestUrl, {
        method,
        headers,
        body: apiBody,
        credentials: config.credentials,
        cache: "no-store",
        redirect: "error",
        signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
      })
    } catch (error) {
      if (options.signal?.aborted) throw error
      throw new Error(
        method === "GET"
          ? "Unable to reach the ticketing system. Please try again."
          : "We could not confirm the change to your cart. Please check your cart before trying again.",
      )
    }

    if (!response.ok) {
      throw new HttpError(response.status, (await response.json().catch(() => null)) ?? {})
    }

    return response
  }
}
